/**
 * Kontoauszugsverarbeitung – Single Source of Truth: SAP B1
 *
 * Eine Seite fuer alles:
 *  - Mandant + Konto-Auswahl (auch mehrere/alle gleichzeitig)
 *  - Pro Kontoauszug eine Tabellenzeile, ausklappbar
 *  - Inline-Edit per Doppelklick (Verwendungszweck, BP-Zuordnung)
 *  - "Zeile buchen" → IncomingPayment in SAP
 *  - "Auszug fest in SAP buchen" → setzt Status auf bssExecuted
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownTrigger,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
  Tooltip,
  useToast,
} from '@/components/ui'
import { Loader2, RefreshCw, CheckCircle2, FileText, Lock, Save, Search, Sparkles, Receipt, Upload, Trash2, BarChart2, X, Edit2, Check, List, Plus, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react'
import { confidenceToneClasses } from '@/styles/colorUtils.ts'

// ─── Types ───────────────────────────────────────────────────────────────────

// Lose typisiert, weil SAP B1 Feldnamen versions-abhaengig sind.
// Helper unten greifen tolerant auf mehrere moegliche Feldnamen zu.
type SapBankStatementRow = Record<string, unknown>
type SapBankStatement = Record<string, unknown> & {
  Status?: 'bssDraft' | 'bssExecuted' | 'bssOld'
  BankStatementRows?: SapBankStatementRow[]
}

interface CrossTenantStatement extends SapBankStatement {
  tenantId: string
  tenantCode: string
  tenantName: string
}

// ─── Field-Helper (versions-tolerant) ───────────────────────────────────────

function getAbsEntry(s: SapBankStatement): number | null {
  const v = (s.InternalNumber ?? s.AbsEntry ?? s.AbsoluteEntry ?? s.DocEntry) as number | undefined
  return typeof v === 'number' ? v : null
}
function getStatementNo(s: SapBankStatement): string {
  const v = (s.StatementNumber ?? s.BankStatementNo ?? s.BankStatementNumber ?? s.Number) as string | number | undefined
  return v != null ? String(v) : ''
}
function getBankCode(s: SapBankStatement): string {
  return String(s.BankCode ?? s.BankCodeName ?? '')
}
function getAccountKey(s: SapBankStatement): number | null {
  const v = (s.BankAccountKey ?? s.AccountKey ?? s.HouseBankAccountKey) as number | undefined
  return typeof v === 'number' ? v : null
}
function getStatementDate(s: SapBankStatement): string {
  const v = (s.StatementDate ?? s.PostingDate ?? s.ToDate ?? s.FromDate) as string | undefined
  return v ? String(v).slice(0, 10) : ''
}
function getStartBalance(s: SapBankStatement): number | null {
  const v = (s.StartingBalanceL ?? s.StartingBalance ?? s.StartingBalanceF ?? s.OpeningBalance ?? s.StartBalance) as number | undefined
  return typeof v === 'number' ? v : null
}
function getEndBalance(s: SapBankStatement): number | null {
  const v = (s.EndingBalanceL ?? s.EndingBalance ?? s.EndingBalanceF ?? s.ClosingBalance ?? s.EndBalance) as number | undefined
  return typeof v === 'number' ? v : null
}
function getCurrency(s: SapBankStatement): string {
  return String(s.Currency ?? 'EUR')
}
function getStatus(s: SapBankStatement): 'bssDraft' | 'bssExecuted' | 'bssOld' {
  return (s.Status as 'bssDraft' | 'bssExecuted' | 'bssOld') ?? 'bssDraft'
}

function getRowField(r: SapBankStatementRow, ...names: string[]): string {
  for (const n of names) {
    const v = r[n]
    if (v != null && v !== '') return String(v)
  }
  return ''
}
function getRowNumber(r: SapBankStatementRow, ...names: string[]): number | null {
  for (const n of names) {
    const v = r[n]
    if (typeof v === 'number') return v
    if (typeof v === 'string' && v !== '') {
      const num = parseFloat(v)
      if (!isNaN(num)) return num
    }
  }
  return null
}
function getRowLineNum(r: SapBankStatementRow): number {
  // SAP B1 nutzt SequenceNo als Row-Identifier (nicht LineNum)
  const v = (r.SequenceNo ?? r.LineNum ?? r.LineNumber ?? r.VisualOrder ?? r.Line) as number | undefined
  return typeof v === 'number' ? v : 0
}
function isRowAlreadyBooked(r: SapBankStatementRow): boolean {
  return r.JournalEntryID != null || r.PaymentID != null || r.ReconciliationNo != null
}
/** Zeile ist zugewiesen (GP oder Sachkonto gesetzt) aber noch nicht in SAP gebucht */
function isRowAssigned(r: SapBankStatementRow): boolean {
  const bp = r.BPCode || r.CardCode || r.CustomerSupplierNo
  const gl = r.AccountNumber
  return !isRowAlreadyBooked(r) && (bp != null || gl != null)
}

interface CrossTenantResponse {
  statements: CrossTenantStatement[]
  errors: Array<{ tenantId: string; tenantCode: string; error: string }>
}


interface BookingPattern {
  id: string
  tenantId: string
  matchCounterparty: string | null
  matchPurpose: string | null
  matchAmountMin: number | null
  matchAmountMax: number | null
  proposedGlAccount: string | null
  proposedBpCode: string | null
  proposedExternalCode: string | null
  proposedInternalCode: string | null
  proposedDescription: string | null
  useCount: number
}

interface PatternSuggestion {
  normalizedName: string
  displayName: string
  occurrences: number
  externalCode: string | null
  externalCodeConfidence: number
  internalCode: string | null
  internalCodeConfidence: number
  glAccount: string | null
  glAccountConfidence: number
  bpCode: string | null
  bpCodeConfidence: number
  variants: Array<{
    externalCode: string | null
    internalCode: string | null
    glAccount: string | null
    bpCode: string | null
    count: number
  }>
}

interface AiSuggestionRecord {
  id: string
  tenantId: string
  absoluteEntry: number
  lineNum: number
  runDate: string
  status: 'pending' | 'approved' | 'rejected' | 'applied'
  counterpartyName: string | null
  purpose: string | null
  amount: string | null
  suggestedBpCode: string | null
  suggestedGlAccount: string | null
  suggestedExternalCode: string | null
  suggestedInternalCode: string | null
  suggestedDescription: string | null
  confidence: number
  aiReasoning: string | null
  matchedPatternId: string | null
  transactionId: string | null
  createdAt: string
}

interface GcNeedsReviewTx {
  id: string
  bookingDate: string
  amount: string
  currency: string
  counterpartyName: string | null
  purpose: string | null
  accountId: string
  proposal: {
    id: string
    proposedCardCode: string | null
    proposedCardName: string | null
    proposedDocNum: number | null
    proposedDocType: string | null
    proposedAmount: string | null
    score: number
    reason: string | null
    status: string
  } | null
  aiSuggestion: {
    id: string
    suggestedGlAccount: string | null
    suggestedBpCode: string | null
    suggestedDescription: string | null
    confidence: number
    aiReasoning: string | null
    status: string
  } | null
}

interface InternalBankOpCode {
  Code: number | string
  Description: string
}

interface HistoryRow {
  statement_abs_entry: number
  statement_date: string | null
  line_num: number
  booking_date: string | null
  counterparty_name: string | null
  purpose: string | null
  amount_debit: string | null
  amount_credit: string | null
  external_code: string | null
  internal_bank_op_code: number | null
  gl_account: string | null
  bp_code: string | null
  bp_name: string | null
}

// ─── Filter-Persistenz (localStorage) ──────────────────────────────────────

const FILTERS_STORAGE_KEY = 'buchhaltung-filters-v1'

/** Liest ein String[]-Feld aus dem gespeicherten Filter-Objekt; Fallback auf ein alternatives Feld. */
function readStoredSeenKeys(primaryKey: keyof StoredFilters, fallbackKey: keyof StoredFilters): Set<string> {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
    if (raw) {
      const s = JSON.parse(raw) as StoredFilters
      const arr = (s[primaryKey] ?? s[fallbackKey]) as string[] | undefined
      if (arr) return new Set(arr)
    }
  } catch { /* ignore */ }
  return new Set()
}

interface StoredFilters {
  tenants?: string[]
  accountKeys?: string[]
  /** Alle bisher gesehenen Tenant-IDs (inkl. manuell deaktivierter) */
  allSeenTenantIds?: string[]
  /** Alle bisher gesehenen Konto-Keys (inkl. manuell deaktivierter) */
  allSeenAccountKeys?: string[]
  statusFilter?: 'open' | 'closed' | 'all'
  expandAll?: boolean
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function BuchhaltungPage() {
  const a = useAnonymize()
  const { fmtMoney: formatMoney } = a
  const tenants = useTenantStore((s) => s.tenants)
  const fetchTenants = useTenantStore((s) => s.fetchTenants)
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  // Globale Mandantenauswahl aus dem Store (via TenantContextBar gesteuert)
  const selectedTenants = useMemo(() => new Set(activeTenantIds), [activeTenantIds])
  const { toast } = useToast()
  const notifyError = useCallback((msg: string) => toast({ title: msg, tone: 'danger' }), [toast])
  const notifySuccess = useCallback((msg: string) => toast({ title: msg, tone: 'success' }), [toast])
  const notifyInfo = useCallback((msg: string) => toast({ title: msg, tone: 'info' }), [toast])
  const [statements, setStatements] = useState<CrossTenantStatement[]>([])
  const [errors, setErrors] = useState<Array<{ tenantId: string; tenantCode: string; error: string }>>([])
  const [loading, setLoading] = useState(false)
  // HouseBankAccount-Lookup pro tenantId+AbsEntry → BankCode/AccountName
  const [houseBanks, setHouseBanks] = useState<Record<string, { code: string; name: string; iban: string }>>({})
  const [selectedAccountKeys, setSelectedAccountKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
      if (raw) {
        const s = JSON.parse(raw) as StoredFilters
        return new Set(s.accountKeys ?? [])
      }
    } catch { /* ignore */ }
    return new Set()
  })
  const [statusFilter, setStatusFilter] = useState<'open' | 'closed' | 'all'>(() => {
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
      if (raw) {
        const s = JSON.parse(raw) as StoredFilters
        if (s.statusFilter === 'open' || s.statusFilter === 'closed' || s.statusFilter === 'all') return s.statusFilter
      }
    } catch { /* ignore */ }
    return 'open'
  })
  const [searchQuery, setSearchQuery] = useState('')
  // Booking-Patterns pro Tenant
  const [_patterns, setPatterns] = useState<BookingPattern[]>([])
  // Pattern-Analyse (Buchungsmuster aus SAP lernen)
  const [showPatternAnalysis, setShowPatternAnalysis] = useState(false)
  const [patternAnalysisTenantId, setPatternAnalysisTenantId] = useState<string>('')
  const [patternAnalysisLoading, setPatternAnalysisLoading] = useState(false)
  const [patternAnalysisResult, setPatternAnalysisResult] = useState<{
    suggestions: PatternSuggestion[]
    totalRows: number
    errors: string[]
  } | null>(null)
  const [patternAnalysisError, setPatternAnalysisError] = useState<string | null>(null)
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set())
  const [patternSaveLoading, setPatternSaveLoading] = useState(false)
  const [patternSaveResult, setPatternSaveResult] = useState<string | null>(null)
  // Muster verwalten
  const [showPatternsManage, setShowPatternsManage] = useState(false)
  const [patternsManageTenantId, setPatternsManageTenantId] = useState<string>('')
  const [patternsManageLoading, setPatternsManageLoading] = useState(false)
  const [patternsManageList, setPatternsManageList] = useState<BookingPattern[]>([])
  const [patternEditId, setPatternEditId] = useState<string | null>(null)
  const [patternEditValues, setPatternEditValues] = useState<Partial<BookingPattern>>({})
  const [patternEditSaving, setPatternEditSaving] = useState(false)
  const [patternDeleteLoading, setPatternDeleteLoading] = useState<string | null>(null)
  // KI-Buchungsvorschlaege
  const AI_RUN_DATE_KEY = 'buchhaltung-ai-last-run'
  const [showAiSuggestions, setShowAiSuggestions] = useState(false)
  const [aiSuggestionTenantId, setAiSuggestionTenantId] = useState<string>('')
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestionRecord[]>([])
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false)
  const [aiAnalysisRunning, setAiAnalysisRunning] = useState(false)
  const [aiAnalysisProgress, setAiAnalysisProgress] = useState<{ done: number; total: number } | null>(null)
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null)
  const [lastAiRunDate, setLastAiRunDate] = useState<string>(() => {
    try { return localStorage.getItem(AI_RUN_DATE_KEY) ?? '' } catch { return '' }
  })
  const [selectedAiIds, setSelectedAiIds] = useState<Set<string>>(new Set())
  const [batchApproving, setBatchApproving] = useState(false)
  const [batchApplying, setBatchApplying] = useState(false)
  const [expandedAiReasoningIds, setExpandedAiReasoningIds] = useState<Set<string>>(new Set())
  const [automationStats, setAutomationStats] = useState<{ autoBooked: number; total: number; rate: number } | null>(null)
  const [automationStatsLoading, setAutomationStatsLoading] = useState(false)
  // GoCardless-Flow: Automatisierungsquote (Modul A+B+C)
  const [bankAutomationRate, setBankAutomationRate] = useState<{
    total: number; booked: number; patternMatched: number; aiSuggested: number
    needsReview: number; ignored: number; automationRate: number
  } | null>(null)
  const [bankAutomationRateLoading, setBankAutomationRateLoading] = useState(false)
  const [gcReviewItems, setGcReviewItems] = useState<GcNeedsReviewTx[]>([])
  const [gcReviewLoading, setGcReviewLoading] = useState(false)
  const [gcApprovingId, setGcApprovingId] = useState<string | null>(null)
  const [showCreatePatternDialog, setShowCreatePatternDialog] = useState(false)
  const [createPatternPrefill, setCreatePatternPrefill] = useState<Partial<BookingPattern> | null>(null)
  const [createPatternSaving, setCreatePatternSaving] = useState(false)
  // Intern-/Extern-Codes pro Tenant – werden bei loadStatements gefuellt
  const [internalCodes, setInternalCodes] = useState<Record<string, InternalBankOpCode[]>>({})
  const [externalCodes, setExternalCodes] = useState<Record<string, InternalBankOpCode[]>>({})
  // Offene Posten pro Tenant (fuer Auto-Match)
  const [openInvoicesByTenant, setOpenInvoicesByTenant] = useState<Record<string, OpenInvoiceLite[]>>({})
  // Row-Booking-Status (laufende Buchungen pro Row fuer Button-Disabled-State)
  const [bookingInProgress, setBookingInProgress] = useState<Set<string>>(new Set())
  // Upload-Dialog
  const [showUpload, setShowUpload] = useState(false)
  // Expand
  const [expandAll, setExpandAll] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
      if (raw) {
        const s = JSON.parse(raw) as StoredFilters
        return !!s.expandAll
      }
    } catch { /* ignore */ }
    return false
  })
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [rowDetails, setRowDetails] = useState<Record<string, SapBankStatement>>({})
  const [loadingDetail, setLoadingDetail] = useState<Set<string>>(new Set())
  // Gecachte Kontoauszuege (fuer Historien-Lookup – key: "tenantId-absEntry")
  const [cachedStmtKeys, setCachedStmtKeys] = useState<Set<string>>(new Set())
  // Inline edit
  const [editingCell, setEditingCell] = useState<{ key: string; lineNum: number; field: string; initialQuery?: string } | null>(null)
  const [showDebug, setShowDebug] = useState(false)
  // Merkt sich bereits gesehene Konto-Keys, damit nach einem Upload neue Konten
  // automatisch aktiviert werden, ohne manuell entfernte wieder hinzuzufuegen.
  // Initial mit ALLEN je gesehenen Account-Keys gefuellt (nicht nur den aktiven),
  // damit deaktivierte Konten nach einem Reload nicht wieder aktiviert werden.
  const seenAccountKeysRef = useRef<Set<string>>(readStoredSeenKeys('allSeenAccountKeys', 'accountKeys'))
  // Gleiches Prinzip fuer Mandanten.

  useEffect(() => { fetchTenants() }, [fetchTenants])

  // Ersten aktiven Mandanten als Standard fuer Pattern-Analyse und KI-Vorschlaege vorauswaehlen
  useEffect(() => {
    if (tenants.length > 0) {
      const first = tenants.find((t) => t.isActive) ?? tenants[0]
      if (first) {
        if (!patternAnalysisTenantId) setPatternAnalysisTenantId(first.id)
        if (!aiSuggestionTenantId) setAiSuggestionTenantId(first.id)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenants])

  useEffect(() => {
    if (aiSuggestionTenantId) loadAutomationStats(aiSuggestionTenantId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSuggestionTenantId])

  // GoCardless-Automatisierungsquote und Review-Items beim Seitenstart laden
  useEffect(() => {
    void loadBankAutomationRate()
    void loadGcReviewItems()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadStatements = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<CrossTenantResponse>('/buchhaltung/sap/statements/cross-tenant', {
        params: { status: statusFilter },
      })
      setStatements(res.data.statements)
      setErrors(res.data.errors)
      // HouseBankAccounts pro Tenant nachladen fuer Bank-Aufloesung
      const distinctTenants = Array.from(new Set(res.data.statements.map((s) => s.tenantId)))
      const hbMap: Record<string, { code: string; name: string; iban: string }> = {}
      await Promise.all(distinctTenants.map(async (tid) => {
        try {
          const hb = await apiClient.get<Array<Record<string, unknown>>>('/buchhaltung/sap/house-bank-accounts', { params: { tenantId: tid } })
          for (const a of hb.data) {
            // BankAccountKey auf Statement-Seite zeigt auf den Primary-Key hier.
            // SAP B1 nutzt je Version andere Namen fuer den Key.
            const ae = (a.AbsEntry ?? a.AbsoluteEntry ?? a.InternalKey ?? a.Key ?? a.BankAcctKey ?? a.InternalNumber) as number | undefined
            if (ae == null) continue
            // Bank-Name aus AccountName nehmen, stoerende Rechtsform-Suffixe abschneiden
            const rawName = String(a.AccountName ?? a.BankName ?? a.Branch ?? '')
            const bankName = rawName.replace(/\s*(e\.?G\.?|AG|GmbH|KG|SE)\s*$/i, '').trim()
            // Kontonummer: bevorzugt Account, sonst letzte 4 der IBAN
            const iban = String(a.IBAN ?? '')
            const account = String(a.Account ?? a.AccountNo ?? '')
            const shortNo = account || (iban ? `..${iban.slice(-4)}` : '')
            hbMap[`${tid}:${ae}`] = {
              code: bankName || String(a.BankCode ?? ''),
              name: shortNo,
              iban,
            }
          }
        } catch { /* ignorieren – Mandant evtl. nicht erreichbar */ }
      }))
      setHouseBanks(hbMap)

      // Patterns + Intern-/Extern-Codes + offene Posten parallel laden
      const allPatterns: BookingPattern[] = []
      const icMap: Record<string, InternalBankOpCode[]> = {}
      const ecMap: Record<string, InternalBankOpCode[]> = {}
      const oiMap: Record<string, OpenInvoiceLite[]> = {}
      await Promise.all(distinctTenants.map(async (tid) => {
        await Promise.all([
          apiClient.get<BookingPattern[]>('/buchhaltung/patterns', { params: { tenantId: tid } })
            .then((p) => { allPatterns.push(...p.data) })
            .catch(() => { /* ignore */ }),
          apiClient.get<InternalBankOpCode[]>('/buchhaltung/sap/internal-bank-op-codes', { params: { tenantId: tid } })
            .then((res) => { icMap[tid] = res.data ?? [] })
            .catch(() => { icMap[tid] = [] }),
          apiClient.get<InternalBankOpCode[]>('/buchhaltung/sap/external-bank-op-codes', { params: { tenantId: tid } })
            .then((res) => { ecMap[tid] = res.data ?? [] })
            .catch(() => { ecMap[tid] = [] }),
          apiClient.get<OpenInvoiceLite[]>('/buchhaltung/sap/open-invoices', { params: { tenantId: tid, type: 'both' } })
            .then((res) => { oiMap[tid] = res.data ?? [] })
            .catch(() => { oiMap[tid] = [] }),
        ])
      }))
      setPatterns(allPatterns)
      setInternalCodes(icMap)
      setExternalCodes(ecMap)
      setOpenInvoicesByTenant(oiMap)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      notifyError(err?.response?.data?.error || err?.message || 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  function bankLabel(s: CrossTenantStatement): string {
    const ak = getAccountKey(s)
    if (ak == null) return getBankCode(s) || '–'
    const hb = houseBanks[`${s.tenantId}:${ak}`]
    if (hb) {
      // hb.code = BLZ, hb.name = Kontoname (z.B. „Geschaeftskonto") – beides
      // keine sensiblen Kundendaten. Nur die IBAN wird anonymisiert.
      const parts = [hb.code, hb.name].filter(Boolean)
      return parts.join(' · ') || a.iban(hb.iban) || `Konto ${ak}`
    }
    return getBankCode(s) || `Konto ${ak}`
  }

  useEffect(() => { loadStatements() }, [loadStatements])

  // Filter-Auswahl in localStorage persistieren (Konten, Status, Expand)
  useEffect(() => {
    try {
      const toStore: StoredFilters = {
        accountKeys: Array.from(selectedAccountKeys),
        allSeenAccountKeys: Array.from(seenAccountKeysRef.current),
        statusFilter,
        expandAll,
      }
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toStore))
    } catch { /* quota / private-mode ignore */ }
  }, [selectedAccountKeys, statusFilter, expandAll])

  // Distinct accounts aus den geladenen Statements (mit HouseBank-Aufloesung)
  const accounts = useMemo(() => {
    const map = new Map<string, { key: string; tenantId: string; tenantCode: string; accountKey: number; label: string }>()
    for (const s of statements) {
      const ak = getAccountKey(s)
      if (ak == null) continue
      const key = `${s.tenantId}:${ak}`
      if (!map.has(key)) {
        const hb = houseBanks[key]
        const bankLabelStr = hb ? ([hb.code, hb.name].filter(Boolean).join(' ') || hb.iban || `Konto ${ak}`) : `Konto ${ak}`
        map.set(key, {
          key,
          tenantId: s.tenantId,
          tenantCode: s.tenantCode,
          accountKey: ak,
          label: `${s.tenantCode} · ${bankLabelStr}`,
        })
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [statements, houseBanks])

  // Init / Update: jede NEU gesehene Konto-Kombo automatisch anhaken
  // (ohne bereits manuell deaktivierte Konten wieder zu aktivieren)
  useEffect(() => {
    if (accounts.length === 0) return
    const newKeys: string[] = []
    for (const a of accounts) {
      if (!seenAccountKeysRef.current.has(a.key)) {
        newKeys.push(a.key)
        seenAccountKeysRef.current.add(a.key)
      }
    }
    if (newKeys.length > 0) {
      setSelectedAccountKeys((prev) => {
        const next = new Set(prev)
        for (const k of newKeys) next.add(k)
        return next
      })
    }
   
  }, [accounts])

  // Gefilterte Statements (nach Mandant, Konto, Freitextsuche) – aktuellstes Datum oben
  // Fuer die Freitextsuche werden auch bereits geladene Detail-Zeilen durchsucht.
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return statements.filter((s) => {
      if (!selectedTenants.has(s.tenantId)) return false
      const ak = getAccountKey(s)
      if (selectedAccountKeys.size > 0 && ak != null && !selectedAccountKeys.has(`${s.tenantId}:${ak}`)) return false
      if (q) {
        const stmtMatch = [getStatementNo(s), getStatementDate(s), s.tenantCode, String(getStartBalance(s) ?? ''), String(getEndBalance(s) ?? '')]
          .join(' ').toLowerCase().includes(q)
        // Wenn Details schon geladen sind → auch Zeilen durchsuchen
        const k = `${s.tenantId}-${getAbsEntry(s) ?? 'x'}`
        const detail = rowDetails[k]
        const rows = (detail?.BankStatementRows ?? []) as SapBankStatementRow[]
        const rowMatch = rows.some((r) => {
          const cp = getRowField(r, 'CustomerSupplierName', 'CardName', 'PartyName').toLowerCase()
          const det = getRowField(r, 'Details', 'Description', 'Memo', 'Remarks').toLowerCase()
          const ref = getRowField(r, 'PaymentReference', 'ExternalCode', 'EndToEndId', 'Reference').toLowerCase()
          const dr = String(getRowNumber(r, 'Debit') ?? '')
          const cr = String(getRowNumber(r, 'Credit') ?? '')
          return cp.includes(q) || det.includes(q) || ref.includes(q) || dr.includes(q) || cr.includes(q)
        })
        if (!stmtMatch && !rowMatch) return false
      }
      return true
    }).sort((a, b) => getStatementDate(b).localeCompare(getStatementDate(a)))
  }, [statements, selectedTenants, selectedAccountKeys, searchQuery, rowDetails])

  function getRowCount(s: SapBankStatement): number | null {
    const k = `${statements[0] ? (s as CrossTenantStatement).tenantId : ''}-${getAbsEntry(s) ?? 'x'}`
    const d = rowDetails[k]
    if (d) return ((d.BankStatementRows as SapBankStatementRow[] | undefined) ?? []).length
    const rows = (s.BankStatementRows as SapBankStatementRow[] | undefined)
    return rows ? rows.length : null
  }

  function rowKey(s: CrossTenantStatement) { return `${s.tenantId}-${getAbsEntry(s) ?? 'unknown'}` }

  async function toggleExpand(s: CrossTenantStatement) {
    const k = rowKey(s)
    const ae = getAbsEntry(s)
    const next = new Set(expandedRows)
    if (next.has(k)) {
      next.delete(k)
      setExpandedRows(next)
      return
    }
    next.add(k)
    setExpandedRows(next)
    if (ae == null) return
    if (!rowDetails[k]) {
      setLoadingDetail((prev) => new Set(prev).add(k))
      try {
        const res = await apiClient.get<SapBankStatement>(`/buchhaltung/sap/statements/${s.tenantId}/${ae}`)
        setRowDetails((prev) => ({ ...prev, [k]: res.data }))
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } }
        notifyError(err?.response?.data?.error || 'Auszug-Detail konnte nicht geladen werden')
      } finally {
        setLoadingDetail((prev) => { const c = new Set(prev); c.delete(k); return c })
      }
    }
    // Finalisierte Auszuege im Hintergrund cachen (fire-and-forget vom Client aus)
    const status = getStatus(s)
    if (status !== 'bssDraft' && !cachedStmtKeys.has(k)) {
      setCachedStmtKeys((prev) => new Set(prev).add(k))
      apiClient.post('/buchhaltung/sap/cache-statement', { tenantId: s.tenantId, absEntry: ae })
        .catch(() => { /* ignore – caching ist optional */ })
    }
  }

  // Auto-Expand toggle
  useEffect(() => {
    if (!expandAll) return
    const allKeys = new Set(filtered.map(rowKey))
    setExpandedRows(allKeys)
    filtered.forEach((s) => {
      const k = rowKey(s)
      if (!rowDetails[k] && !loadingDetail.has(k)) {
        toggleExpand(s)
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll, filtered.length])

  // Bei aktiver Suche: Details aller sichtbaren Statements lazy nachladen,
  // damit die Volltextsuche auch ueber Zeilen matcht
  useEffect(() => {
    if (!searchQuery.trim()) return
    statements.forEach((s) => {
      const k = rowKey(s)
      if (!rowDetails[k] && !loadingDetail.has(k)) {
        const ae = getAbsEntry(s)
        if (ae == null) return
        setLoadingDetail((prev) => new Set(prev).add(k))
        apiClient.get<SapBankStatement>(`/buchhaltung/sap/statements/${s.tenantId}/${ae}`)
          .then((res) => setRowDetails((prev) => ({ ...prev, [k]: res.data })))
          .catch(() => { /* ignore */ })
          .finally(() => setLoadingDetail((prev) => { const c = new Set(prev); c.delete(k); return c }))
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statements])

  async function finalizeStatement(s: CrossTenantStatement) {
    const ae = getAbsEntry(s)
    if (ae == null) { notifyError('AbsEntry des Auszugs unbekannt'); return }
    if (!confirm(`Auszug ${getStatementNo(s) || ae} (${s.tenantCode}) fest in SAP buchen?\n\nDanach kann er nicht mehr bearbeitet werden.`)) return
    try {
      await apiClient.post(`/buchhaltung/sap/statements/${s.tenantId}/${ae}/finalize`)
      await loadStatements()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      notifyError(err?.response?.data?.error || 'Buchung fehlgeschlagen')
    }
  }

  async function deleteStatement(s: CrossTenantStatement) {
    const ae = getAbsEntry(s)
    if (ae == null) return
    if (!confirm(`Auszug ${getStatementNo(s) || ae} (${s.tenantCode}) aus SAP LÖSCHEN?\n\nNur Entwürfe (bssDraft) können gelöscht werden.`)) return
    try {
      await apiClient.delete(`/buchhaltung/sap/statements/${s.tenantId}/${ae}`)
      await loadStatements()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      const errMsg = err?.response?.data?.error || ''
      // SAP code -10: ein Auszug mit hoeherer Nummer existiert noch
      if (errMsg.includes('higher number') || errMsg.includes('code -10')) {
        if (confirm(`SAP erfordert, dass neuere Entwürfe dieses Kontos zuerst gelöscht werden.\n\nSollen alle neueren Entwürfe dieses Kontos automatisch mitgelöscht werden?`)) {
          try {
            const res = await apiClient.delete<{ ok: boolean; deleted: number[] }>(
              `/buchhaltung/sap/statements/${s.tenantId}/${ae}?cascade=true`,
            )
            const count = res.data.deleted?.length ?? 1
            if (count > 1) notifyInfo(`${count} Auszüge gelöscht (inkl. ${count - 1} neuere${count - 1 > 1 ? 'r' : ''} Entwurf${count - 1 > 1 ? 'e' : ''}).`)
            await loadStatements()
          } catch (e2) {
            const err2 = e2 as { response?: { data?: { error?: string } } }
            notifyError(err2?.response?.data?.error || 'Cascade-Löschen fehlgeschlagen')
          }
        }
      } else {
        notifyError(errMsg || 'Löschen fehlgeschlagen')
      }
    }
  }

  const [autoMatchInProgress, setAutoMatchInProgress] = useState<Set<string>>(new Set())

  async function autoMatchBp(s: CrossTenantStatement) {
    const ae = getAbsEntry(s)
    if (ae == null) return
    const k = rowKey(s)
    setAutoMatchInProgress((prev) => new Set(prev).add(k))
    try {
      const res = await apiClient.post<{
        ok: boolean; matched: number; skipped: number; total: number;
        details: Array<{ lineNum: number; cardCode: string; cardName: string }>
        statement: SapBankStatement
      }>(`/buchhaltung/sap/statements/${s.tenantId}/${ae}/auto-match-bp`)
      // Aktualisiertes Statement mit neuen BPCodes uebernehmen
      if (res.data.statement) {
        setRowDetails((prev) => ({ ...prev, [k]: res.data.statement }))
      }
      if (res.data.matched > 0) {
        notifySuccess(`GP-Auto-Match: ${res.data.matched} Zeile(n) zugewiesen`)
      } else {
        notifyInfo('GP-Auto-Match: Keine eindeutigen Treffer gefunden')
      }
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      notifyError(err?.response?.data?.error || 'Auto-Match fehlgeschlagen')
    } finally {
      setAutoMatchInProgress((prev) => { const c = new Set(prev); c.delete(k); return c })
    }
  }

  async function autoPostStatement(s: CrossTenantStatement) {
    const ae = getAbsEntry(s)
    if (ae == null) return
    const k = rowKey(s)
    setAutoMatchInProgress((prev) => new Set(prev).add(k))
    try {
      const res = await apiClient.post<{
        ok: boolean; booked: number; skipped: number; errors: number; total: number;
        actionable?: number; matchRate?: number; statement: SapBankStatement
        details?: { skipped: Array<{ lineNum: number; reason: string }> }
      }>(`/buchhaltung/sap/statements/${s.tenantId}/${ae}/auto-post`)
      if (res.data.statement) {
        setRowDetails((prev) => ({ ...prev, [k]: res.data.statement }))
      }
      const { booked, actionable, matchRate, errors: errCount, details } = res.data
      const rateStr = matchRate != null ? ` (${matchRate}%)` : ''
      const title = `Auto-Buchen: ${booked} von ${actionable ?? res.data.total} Zeilen zugeordnet${rateStr}`
        + (errCount > 0 ? ` – ${errCount} Fehler` : '')
      if (booked === 0 && details?.skipped?.length) {
        const reasonCounts = new Map<string, number>()
        for (const sk of details.skipped) reasonCounts.set(sk.reason, (reasonCounts.get(sk.reason) ?? 0) + 1)
        const reasonSummary = Array.from(reasonCounts.entries()).map(([r, n]) => `${n}× ${r}`).join(' | ')
        toast({ title, description: reasonSummary, tone: errCount > 0 ? 'danger' : 'info' })
      } else if (errCount > 0) {
        notifyError(title)
      } else {
        notifySuccess(title)
      }
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      notifyError(err?.response?.data?.error || 'Auto-Buchen fehlgeschlagen')
    } finally {
      setAutoMatchInProgress((prev) => { const c = new Set(prev); c.delete(k); return c })
    }
  }

  async function runPatternAnalysis() {
    if (!patternAnalysisTenantId) return
    setPatternAnalysisLoading(true)
    setPatternAnalysisError(null)
    setPatternAnalysisResult(null)
    setSelectedSuggestions(new Set())
    setPatternSaveResult(null)
    try {
      const res = await apiClient.get<{ suggestions: PatternSuggestion[]; totalRows: number; errors: string[] }>(
        '/buchhaltung/sap/pattern-analysis',
        { params: { tenantId: patternAnalysisTenantId, monthsBack: 24 } },
      )
      setPatternAnalysisResult(res.data)
      // Alle vorauswaehlen die mindestens ein Feld gesetzt haben
      const allKeys = new Set(res.data.suggestions.map((s) => s.normalizedName))
      setSelectedSuggestions(allKeys)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setPatternAnalysisError(err?.response?.data?.error || 'Analyse fehlgeschlagen')
    } finally {
      setPatternAnalysisLoading(false)
    }
  }

  async function saveSelectedPatterns() {
    if (!patternAnalysisTenantId || !patternAnalysisResult) return
    const toSave = patternAnalysisResult.suggestions.filter((s) => selectedSuggestions.has(s.normalizedName))
    if (toSave.length === 0) return
    setPatternSaveLoading(true)
    setPatternSaveResult(null)
    try {
      const res = await apiClient.post<{ ok: boolean; saved: number; updated: number }>(
        '/buchhaltung/sap/pattern-analysis/save',
        {
          tenantId: patternAnalysisTenantId,
          patterns: toSave.map((s) => ({
            matchCounterparty: s.displayName,
            proposedGlAccount: s.glAccount,
            proposedBpCode: s.bpCode,
            proposedExternalCode: s.externalCode,
            proposedInternalCode: s.internalCode,
          })),
        },
      )
      setPatternSaveResult(`${res.data.saved} neue Muster gespeichert, ${res.data.updated} aktualisiert`)
      // Patterns neu laden
      const p = await apiClient.get<BookingPattern[]>('/buchhaltung/patterns', { params: { tenantId: patternAnalysisTenantId } })
      setPatterns(p.data)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setPatternSaveResult(`Fehler: ${err?.response?.data?.error || 'unbekannt'}`)
    } finally {
      setPatternSaveLoading(false)
    }
  }

  async function loadPatternsManage(tid?: string) {
    const tenantId = tid ?? patternsManageTenantId
    if (!tenantId) return
    setPatternsManageLoading(true)
    try {
      const res = await apiClient.get<BookingPattern[]>('/buchhaltung/patterns', { params: { tenantId } })
      setPatternsManageList(res.data)
    } catch { /* ignore */ } finally {
      setPatternsManageLoading(false)
    }
  }

  async function savePatternEdit() {
    if (!patternEditId) return
    setPatternEditSaving(true)
    try {
      await apiClient.patch(`/buchhaltung/patterns/${patternEditId}`, patternEditValues)
      setPatternsManageList((prev) => prev.map((p) => p.id === patternEditId ? { ...p, ...patternEditValues } : p))
      setPatternEditId(null)
      setPatternEditValues({})
    } catch { /* ignore */ } finally {
      setPatternEditSaving(false)
    }
  }

  async function deletePattern(id: string) {
    setPatternDeleteLoading(id)
    try {
      await apiClient.delete(`/buchhaltung/patterns/${id}`)
      setPatternsManageList((prev) => prev.filter((p) => p.id !== id))
    } catch { /* ignore */ } finally {
      setPatternDeleteLoading(null)
    }
  }

  async function loadAiSuggestions(tenantId: string) {
    if (!tenantId) return
    setAiSuggestionsLoading(true)
    try {
      const res = await apiClient.get<AiSuggestionRecord[]>('/buchhaltung/sap/ai-suggestions', { params: { tenantId } })
      setAiSuggestions(res.data)
    } catch { /* ignore */ }
    finally { setAiSuggestionsLoading(false) }
  }

  /**
   * Schritt-fuer-Schritt KI-Analyse aller unbuchten Zeilen des gewaehlten Mandanten.
   * Per CLAUDE.md: kein Background-Job – jede Zeile ist ein eigener Request.
   */
  async function runAiAnalysis() {
    if (!aiSuggestionTenantId) return
    setAiAnalysisRunning(true)
    setAiAnalysisError(null)
    setAiAnalysisProgress(null)

    try {
      // Alle Statements des gewaehlten Mandanten aus dem aktuellen State
      const tenantStatements = statements.filter((s) => s.tenantId === aiSuggestionTenantId)

      // Ungebuchte Zeilen sammeln (aus den bereits geladenen rowDetails oder Statement selbst)
      interface PendingRow { absoluteEntry: number; lineNum: number; counterpartyName: string | null; purpose: string | null; amount: number; date: string }
      const pendingRows: PendingRow[] = []

      for (const stmt of tenantStatements) {
        const ae = getAbsEntry(stmt)
        if (ae == null) continue
        const key = rowKey(stmt)
        let rows: SapBankStatementRow[] = []
        if (rowDetails[key]?.BankStatementRows) {
          rows = rowDetails[key].BankStatementRows as SapBankStatementRow[]
        } else {
          try {
            const r = await apiClient.get<SapBankStatement>(`/buchhaltung/sap/statements/${stmt.tenantId}/${ae}`)
            rows = (r.data.BankStatementRows ?? []) as SapBankStatementRow[]
            setRowDetails((prev) => ({ ...prev, [key]: r.data }))
          } catch { continue }
        }
        for (const row of rows) {
          // Nur ungebuchte Zeilen (kein JournalEntry, keine Zahlung, keine Abstimmung)
          if (isRowAlreadyBooked(row)) continue

          const lineNum = getRowLineNum(row)
          const credit = getRowNumber(row, 'CreditAmountLC', 'CreditAmountFC', 'Credit') ?? 0
          const debit = getRowNumber(row, 'DebitAmountLC', 'DebitAmountFC', 'Debit') ?? 0
          pendingRows.push({
            absoluteEntry: ae,
            lineNum,
            counterpartyName: getRowField(row, 'Remarks', 'ShortName', 'Counterparty', 'CounterpartyName') || null,
            purpose: getRowField(row, 'Details', 'Description', 'Narration', 'Reference') || null,
            amount: credit > 0 ? credit : -debit,
            date: getRowField(row, 'PostingDate', 'DueDate', 'ValueDate') || new Date().toISOString().slice(0, 10),
          })
        }
      }

      if (pendingRows.length === 0) {
        setAiAnalysisError('Keine unbuchten Zeilen für diesen Mandanten gefunden.')
        return
      }

      setAiAnalysisProgress({ done: 0, total: pendingRows.length })

      const today = new Date().toISOString().slice(0, 10)
      const newSuggestions: AiSuggestionRecord[] = []

      for (let i = 0; i < pendingRows.length; i++) {
        const row = pendingRows[i]!
        try {
          const res = await apiClient.post<AiSuggestionRecord>('/buchhaltung/sap/ai-suggestions/analyze-row', {
            tenantId: aiSuggestionTenantId,
            absoluteEntry: row.absoluteEntry,
            lineNum: row.lineNum,
            counterpartyName: row.counterpartyName,
            purpose: row.purpose,
            amount: row.amount,
            date: row.date,
          })
          newSuggestions.push(res.data)
        } catch { /* einzelne Zeile ignorieren */ }
        setAiAnalysisProgress({ done: i + 1, total: pendingRows.length })
      }

      // Laufdatum merken
      localStorage.setItem(AI_RUN_DATE_KEY, today)
      setLastAiRunDate(today)

      // Alle Vorschlaege neu laden (inkl. heute schon vorhandene)
      await loadAiSuggestions(aiSuggestionTenantId)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      setAiAnalysisError(err?.response?.data?.error || err?.message || 'Analyse fehlgeschlagen')
    } finally {
      setAiAnalysisRunning(false)
      setAiAnalysisProgress(null)
    }
  }

  async function approveAiSuggestion(id: string) {
    try {
      await apiClient.post(`/buchhaltung/sap/ai-suggestions/${id}/approve`, {})
      setAiSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, status: 'approved' } : s))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      notifyError(`Genehmigung fehlgeschlagen: ${msg}`)
    }
  }

  async function rejectAiSuggestion(id: string) {
    try {
      await apiClient.post(`/buchhaltung/sap/ai-suggestions/${id}/reject`, {})
      setAiSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, status: 'rejected' } : s))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      notifyError(`Ablehnung fehlgeschlagen: ${msg}`)
    }
  }

  async function batchApproveAiSuggestions() {
    if (selectedAiIds.size === 0) return
    setBatchApproving(true)
    try {
      await apiClient.post('/buchhaltung/sap/ai-suggestions/batch-approve', { ids: Array.from(selectedAiIds) })
      setAiSuggestions((prev) => prev.map((s) => selectedAiIds.has(s.id) ? { ...s, status: 'approved' } : s))
      setSelectedAiIds(new Set())
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setAiAnalysisError(`Batch-Genehmigung fehlgeschlagen: ${msg}`)
    } finally {
      setBatchApproving(false)
    }
  }

  async function batchApplyAiSuggestions() {
    if (!aiSuggestionTenantId) return
    setBatchApplying(true)
    setAiAnalysisError(null)
    try {
      const res = await apiClient.post<{ ok: boolean; applied: number; skipped: number; errors: number; patterns: number }>(
        `/buchhaltung/sap/ai-suggestions/batch-apply?tenantId=${aiSuggestionTenantId}`,
        {},
      )
      const { applied, skipped, errors, patterns } = res.data
      const parts = [`${applied} gebucht`]
      if (patterns > 0) parts.push(`${patterns} neue Muster`)
      if (skipped > 0) parts.push(`${skipped} übersprungen`)
      if (errors > 0) parts.push(`${errors} Fehler`)
      notifySuccess(parts.join(' · '))
      await loadAiSuggestions(aiSuggestionTenantId)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      setAiAnalysisError(err?.response?.data?.error || err?.message || 'Buchung fehlgeschlagen')
    } finally {
      setBatchApplying(false)
    }
  }

  async function loadAutomationStats(tenantId: string) {
    setAutomationStatsLoading(true)
    try {
      const res = await apiClient.get<{ autoBooked: number; total: number; rate: number }>('/buchhaltung/sap/automation-stats', { params: { tenantId } })
      setAutomationStats(res.data)
    } catch { setAutomationStats(null) } finally { setAutomationStatsLoading(false) }
  }

  async function loadBankAutomationRate() {
    setBankAutomationRateLoading(true)
    try {
      type BankRateResp = { total: number; booked: number; patternMatched: number; aiSuggested: number; needsReview: number; ignored: number; automationRate: number }
      const res = await apiClient.get<BankRateResp>('/buchhaltung/bank/automation-rate')
      setBankAutomationRate(res.data)
    } catch { setBankAutomationRate(null) } finally { setBankAutomationRateLoading(false) }
  }

  async function loadGcReviewItems() {
    setGcReviewLoading(true)
    try {
      const res = await apiClient.get<{ transactions: GcNeedsReviewTx[] }>('/buchhaltung/bank/needs-review')
      setGcReviewItems(res.data.transactions)
    } catch { setGcReviewItems([]) } finally { setGcReviewLoading(false) }
  }

  async function approveGcProposal(txId: string, proposalId: string) {
    setGcApprovingId(proposalId)
    try {
      await apiClient.post(`/buchhaltung/proposals/${proposalId}/approve`, {})
      setGcReviewItems((prev) => prev.filter((tx) => tx.id !== txId))
      void loadBankAutomationRate()
    } catch (e) { notifyError(`Buchung fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`) }
    finally { setGcApprovingId(null) }
  }

  async function rejectGcProposal(txId: string, proposalId: string) {
    setGcApprovingId(proposalId)
    try {
      await apiClient.post(`/buchhaltung/proposals/${proposalId}/reject`, {})
      setGcReviewItems((prev) => prev.filter((tx) => tx.id !== txId))
    } catch (e) { notifyError(`Ablehnen fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`) }
    finally { setGcApprovingId(null) }
  }

  async function acceptGcAiSuggestion(txId: string, suggestionId: string) {
    setGcApprovingId(suggestionId)
    try {
      await apiClient.post(`/buchhaltung/bank/ai-suggestions/${suggestionId}/accept`, {})
      setGcReviewItems((prev) => prev.filter((tx) => tx.id !== txId))
      void loadBankAutomationRate()
    } catch (e) { notifyError(`KI-Vorschlag akzeptieren fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`) }
    finally { setGcApprovingId(null) }
  }

  async function rejectGcAiSuggestion(txId: string, suggestionId: string) {
    setGcApprovingId(suggestionId)
    try {
      await apiClient.post(`/buchhaltung/bank/ai-suggestions/${suggestionId}/reject`, {})
      setGcReviewItems((prev) => prev.filter((tx) => tx.id !== txId))
    } catch (e) { notifyError(`KI-Vorschlag ablehnen fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`) }
    finally { setGcApprovingId(null) }
  }

  async function saveCreatedPattern(pattern: Partial<BookingPattern>) {
    if (!pattern.tenantId) return
    setCreatePatternSaving(true)
    try {
      await apiClient.post('/buchhaltung/patterns', pattern)
      setShowCreatePatternDialog(false)
      setCreatePatternPrefill(null)
      notifySuccess('Muster gespeichert')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      notifyError(`Muster speichern fehlgeschlagen: ${msg}`)
    } finally {
      setCreatePatternSaving(false)
    }
  }

  async function refreshInternCodesForTenant(tenantId: string) {
    try {
      const res = await apiClient.get<InternalBankOpCode[]>('/buchhaltung/sap/internal-bank-op-codes', { params: { tenantId, refresh: '1' } })
      setInternalCodes((prev) => ({ ...prev, [tenantId]: res.data ?? [] }))
      notifySuccess(`Intern-Codes aktualisiert (${res.data?.length ?? 0} Einträge)`)
    } catch (e) {
      notifyError((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Intern-Codes konnten nicht geladen werden')
    }
  }
  async function refreshExternCodesForTenant(tenantId: string) {
    try {
      const res = await apiClient.get<InternalBankOpCode[]>('/buchhaltung/sap/external-bank-op-codes', { params: { tenantId, refresh: '1' } })
      setExternalCodes((prev) => ({ ...prev, [tenantId]: res.data ?? [] }))
    } catch { /* ignore */ }
  }

  async function saveCellEdit(s: CrossTenantStatement, lineNum: number, field: string, value: string, extraPatch?: Record<string, string>) {
    const ae = getAbsEntry(s)
    if (ae == null) { notifyInfo('[DEBUG] saveCellEdit: ae is null'); return }
    notifyInfo(`[DEBUG] PATCH /rows/${lineNum} field=${field} value=${value}`)
    try {
      await apiClient.patch(`/buchhaltung/sap/statements/${s.tenantId}/${ae}/rows/${lineNum}`, {
        [field]: value,
        ...extraPatch,
      })
      const res = await apiClient.get<SapBankStatement>(`/buchhaltung/sap/statements/${s.tenantId}/${ae}`)
      setRowDetails((prev) => ({ ...prev, [rowKey(s)]: res.data }))
      setEditingCell(null)
      notifySuccess(`[DEBUG] PATCH Erfolg: field=${field} value=${value} an Zeile ${lineNum}`)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string }; status?: number }; message?: string }
      notifyError(`[DEBUG] PATCH Error: ${err?.response?.status ?? 'no status'} – ${err?.response?.data?.error || err?.message || 'unbekannt'}`)
    }
  }


  /**
   * Direkt buchen (ohne Modal):
   *   - Wenn invoice gegeben → Zahlungseingang/-ausgang in SAP anlegen,
   *     die Rechnung ausgleichen
   *   - Sonst → BP/GL direkt an die Row patchen
   */
  async function bookRowInline(
    s: CrossTenantStatement,
    row: SapBankStatementRow,
    params: { invoice?: OpenInvoiceLite; cashDiscount?: number } = {},
  ) {
    const ae = getAbsEntry(s)
    if (ae == null) return
    const lineNum = getRowLineNum(row)
    const rowId = `${s.tenantId}-${ae}-${lineNum}`
    setBookingInProgress((prev) => new Set(prev).add(rowId))
    try {
      const credit = getRowNumber(row, 'CreditAmountLC', 'CreditAmountFC', 'Credit') ?? 0
      const debit = getRowNumber(row, 'DebitAmountLC', 'DebitAmountFC', 'Debit') ?? 0
      const amount = credit > 0 ? credit : debit
      const bookingDate = getRowField(row, 'BankStmtLineDate', 'PostingDate').slice(0, 10) || undefined
      const description = getRowField(row, 'Details', 'Details2')

      if (params.invoice) {
        const bookRes = await apiClient.post<{ ok: boolean; alreadyClosed?: boolean; message?: string }>(
          `/buchhaltung/sap/statements/${s.tenantId}/${ae}/rows/${lineNum}/book-invoice`,
          {
            invoiceDocEntry: params.invoice.DocEntry,
            invoiceDocType: params.invoice.DocType,
            cardCode: params.invoice.CardCode,
            cardName: params.invoice.CardName,
            docCurrency: params.invoice.DocCurrency,
            docTotal: params.invoice.DocTotal,
            amount,
            cashDiscount: params.cashDiscount,
            bookingDate,
            remarks: description?.slice(0, 200) || `Zahlung zu ${params.invoice.DocType === 'Invoice' ? 'AR' : 'ER'} #${params.invoice.DocNum}`,
          },
        )
        if (bookRes.data?.alreadyClosed) {
          // Rechnung war in SAP schon geschlossen – Zeile wurde trotzdem
          // mit BPCode gepatcht. Offene Posten fuer diesen Tenant neu laden.
          try {
            const oiRes = await apiClient.get<OpenInvoiceLite[]>('/buchhaltung/sap/open-invoices', { params: { tenantId: s.tenantId, type: 'both' } })
            setOpenInvoicesByTenant((prev) => ({ ...prev, [s.tenantId]: oiRes.data ?? [] }))
          } catch { /* ignorieren */ }
        }
      } else {
        // Kein Invoice-Match – direkte Row-Buchung per Sachkonto / BP.
        // Die Werte werden direkt aus der Row gelesen (durch vorherige
        // Inline-Edits bereits PATCHed).
        const glAccount = getRowField(row, 'AccountNumber')
        const bpCode = getRowField(row, 'BPCode')
        if (!glAccount && !bpCode) {
          notifyError(`Zeile ${lineNum}: Bitte Sachkonto oder GP-Code angeben, bevor gebucht werden kann.`)
          return
        }
        await apiClient.post(
          `/buchhaltung/sap/statements/${s.tenantId}/${ae}/rows/${lineNum}/book`,
          {
            glAccount: glAccount || undefined,
            bpCode: bpCode || undefined,
            description: description || undefined,
            savePattern: false,
          },
        )
      }

      // Detail neu laden damit die Zeile als "gebucht" markiert wird
      const res = await apiClient.get<SapBankStatement>(`/buchhaltung/sap/statements/${s.tenantId}/${ae}`)
      setRowDetails((prev) => ({ ...prev, [rowKey(s)]: res.data }))
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      notifyError(err?.response?.data?.error || 'Buchung fehlgeschlagen')
    } finally {
      setBookingInProgress((prev) => { const c = new Set(prev); c.delete(rowId); return c })
    }
  }

  function toggleAccount(key: string) {
    const next = new Set(selectedAccountKeys)
    if (next.has(key)) next.delete(key); else next.add(key)
    setSelectedAccountKeys(next)
  }

  const today = new Date().toISOString().slice(0, 10)
  const aiRunDue = lastAiRunDate !== today

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-4">
      <PageHeader
        title="Kontoauszugsverarbeitung"
        description="Bank-Statements aus SAP B1 – mandantenübergreifend, mit Inline-Bearbeitung und SAP-Verbuchung"
        actions={
          <>
            <Button
              variant="secondary"
              leadingIcon={<Sparkles />}
              className="relative"
              onClick={() => {
                if (!aiSuggestionTenantId && tenants.length > 0) {
                  setAiSuggestionTenantId(tenants.find((t) => t.isActive)?.id ?? tenants[0]!.id)
                }
                setShowAiSuggestions(true)
                if (aiSuggestionTenantId) loadAiSuggestions(aiSuggestionTenantId)
              }}
            >
              KI-Vorschläge
              {aiRunDue && (
                <span
                  className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border border-white"
                  title="Tägliche Analyse steht aus"
                />
              )}
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<BarChart2 />}
              onClick={() => {
                if (!patternAnalysisTenantId && tenants.length > 0) {
                  setPatternAnalysisTenantId(tenants.find((t) => t.isActive)?.id ?? tenants[0]!.id)
                }
                setShowPatternAnalysis(true)
              }}
            >
              Buchungsmuster analysieren
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<List />}
              onClick={() => {
                const defaultTenant = patternAnalysisTenantId || (tenants.find((t) => t.isActive)?.id ?? tenants[0]?.id ?? '')
                setPatternsManageTenantId(defaultTenant)
                setShowPatternsManage(true)
                loadPatternsManage(defaultTenant)
              }}
            >
              Muster verwalten
            </Button>
            <Button
              variant="primary"
              leadingIcon={<Upload />}
              onClick={() => setShowUpload(true)}
            >
              Kontoauszug hochladen
            </Button>
            <Button
              variant="secondary"
              leadingIcon={loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              disabled={loading}
              onClick={loadStatements}
            >
              Aktualisieren
            </Button>
          </>
        }
      />

      {/* Filter-Leiste */}
      <Card>
        <Card.Body className="space-y-3">
          {accounts.length > 0 && (
            <div>
              <div className="text-caption font-medium text-gray-500 mb-1.5 flex items-center justify-between">
                <span>Konten ({selectedAccountKeys.size}/{accounts.length})</span>
                <div className="flex gap-2">
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() => setSelectedAccountKeys(new Set(accounts.map((a) => a.key)))}
                  >
                    Alle
                  </Button>
                  <Button
                    variant="link"
                    size="xs"
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setSelectedAccountKeys(new Set())}
                  >
                    Keins
                  </Button>
                </div>
              </div>
              <FilterBar className="border-0 shadow-none p-0 bg-transparent">
                {accounts.map((acc) => (
                  <FilterChip
                    key={acc.key}
                    active={selectedAccountKeys.has(acc.key)}
                    onClick={() => toggleAccount(acc.key)}
                  >
                    {acc.label}
                  </FilterChip>
                ))}
              </FilterBar>
            </div>
          )}

          <div className="flex items-center gap-4 pt-2 border-t border-gray-100 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-caption text-gray-500">Status:</span>
              <FilterBar className="border-0 shadow-none p-0 bg-transparent">
                {(['open', 'closed', 'all'] as const).map((s) => (
                  <FilterChip
                    key={s}
                    active={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s === 'open' ? 'Nur offen' : s === 'closed' ? 'Nur abgeschlossen' : 'Alle'}
                  </FilterChip>
                ))}
              </FilterBar>
            </div>
            <Checkbox
              checked={expandAll}
              onChange={(e) => setExpandAll(e.currentTarget.checked)}
              label={<span className="text-small text-gray-600">Standardmäßig ausgeklappt</span>}
            />
            <div className="flex-1 min-w-[200px] max-w-md ml-auto">
              <Input
                leadingIcon={<Search />}
                placeholder="Suche: Empfänger, Verwendungszweck, Betrag, Referenz…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
              />
            </div>
            <span className="text-caption text-gray-400">{filtered.length} Auszüge</span>
          </div>
        </Card.Body>
      </Card>

      {/* Debug-Anzeige der Roh-Felder eines SAP-Statements */}
      {statements.length > 0 && (
        <div>
          <Button
            variant="link"
            size="xs"
            className="text-caption text-gray-400 hover:text-gray-600"
            onClick={() => setShowDebug(!showDebug)}
          >
            {showDebug ? 'Debug ausblenden' : 'Debug: Roh-SAP-Felder anzeigen'}
          </Button>
          {showDebug && (
            <Card className="mt-2">
              <Card.Body className="space-y-2 text-[10px]">
                <div>
                  <div className="text-gray-500 mb-1">Erster Kontoauszug (Header):</div>
                  <pre className="p-3 bg-gray-900 text-green-300 rounded overflow-x-auto max-h-48 overflow-y-auto">
                    {JSON.stringify(statements[0], null, 2)}
                  </pre>
                </div>
                {(() => {
                  const firstDetail = Object.values(rowDetails)[0]
                  const firstRow = (firstDetail?.BankStatementRows as SapBankStatementRow[] | undefined)?.[0]
                  if (!firstRow) return (
                    <div className="text-gray-400 italic">Klapp einen Auszug auf, um die Roh-Felder einer Buchungszeile anzuzeigen.</div>
                  )
                  return (
                    <div>
                      <div className="text-gray-500 mb-1">Erste Buchungszeile:</div>
                      <pre className="p-3 bg-gray-900 text-yellow-300 rounded overflow-x-auto max-h-48 overflow-y-auto">
                        {JSON.stringify(firstRow, null, 2)}
                      </pre>
                    </div>
                  )
                })()}
                {Object.values(houseBanks).length > 0 && (
                  <div>
                    <div className="text-gray-500 mb-1">HouseBankAccounts-Lookup (erste 3):</div>
                    <pre className="p-3 bg-gray-900 text-cyan-300 rounded overflow-x-auto max-h-48 overflow-y-auto">
                      {JSON.stringify(Object.entries(houseBanks).slice(0, 3), null, 2)}
                    </pre>
                  </div>
                )}
                {/* Bank-Op-Codes Diagnose */}
                {tenants.length > 0 && (
                  <div>
                    <Button
                      variant="link"
                      size="xs"
                      className="text-caption"
                      onClick={async () => {
                        try {
                          const tid = tenants[0]!.id
                          const res = await apiClient.get(`/buchhaltung/sap/bank-op-codes-diagnose`, { params: { tenantId: tid } })
                          notifyInfo(`[DIAGNOSE] Intern: ${JSON.stringify(res.data.internal.codes.slice(0, 5))} ... (${res.data.internal.codes.length} total, errors: ${res.data.internal.errors.join('; ') || 'keine'}) | Extern: ${res.data.external.codes.length} codes, errors: ${res.data.external.errors.join('; ') || 'keine'}`)
                        } catch (e) {
                          notifyError(`[DIAGNOSE] Error: ${(e as Error).message}`)
                        }
                      }}
                    >
                      Bank-Op-Codes Diagnose laden (Intern/Extern)
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="space-y-2">
          {errors.map((e) => (
            <ErrorState
              key={e.tenantId}
              size="sm"
              title={`${e.tenantCode}: Fehler beim Laden`}
              description={e.error}
            />
          ))}
        </div>
      )}

      {loading && <LoadingState />}

      {!loading && filtered.length === 0 && (
        <Card>
          <EmptyState
            title="Keine Kontoauszüge für die aktuelle Auswahl."
            icon={<FileText />}
          />
        </Card>
      )}

      {automationStats && (
        <Card>
          <Card.Body>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-brand-700">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold">Automatisierungsquote (letzte 30 Tage)</span>
              </div>
              {automationStatsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : (
                <div className="flex items-center gap-3">
                  <Badge variant={automationStats.rate >= 70 ? 'success' : automationStats.rate >= 40 ? 'warning' : 'danger'} size="sm">
                    {automationStats.rate}%
                  </Badge>
                  <span className="text-caption text-gray-500">{automationStats.autoBooked} von {automationStats.total} Vorschlägen automatisch gebucht</span>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {automationStats && (
        <Card>
          <Card.Body>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-brand-700">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold">Automatisierungsquote (letzte 30 Tage)</span>
              </div>
              {automationStatsLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : (
                <div className="flex items-center gap-3">
                  <Badge variant={automationStats.rate >= 70 ? 'success' : automationStats.rate >= 40 ? 'warning' : 'danger'} size="sm">
                    {automationStats.rate}%
                  </Badge>
                  <span className="text-caption text-gray-500">{automationStats.autoBooked} von {automationStats.total} Vorschlägen automatisch gebucht</span>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* GoCardless-Automatisierungsquote (Ziel: 95%) — immer anzeigen wenn GoCardless aktiv ist */}
      {(bankAutomationRate !== null || bankAutomationRateLoading) && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-brand-700">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold">GoCardless — Automatisierungsquote (letzte 30 Tage)</span>
                <span className="text-caption text-gray-400 font-normal">Ziel: 95%</span>
              </div>
              {bankAutomationRateLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : bankAutomationRate ? (
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge
                    variant={bankAutomationRate.automationRate >= 90 ? 'success' : bankAutomationRate.automationRate >= 60 ? 'warning' : 'danger'}
                    size="sm"
                  >
                    {bankAutomationRate.automationRate}%
                  </Badge>
                  <div className="flex gap-3 text-caption text-gray-500">
                    <span title="Vollautomatisch über SAP-Abgleich gebucht">✓ {bankAutomationRate.booked} gebucht</span>
                    <span title="Buchungsmuster auto-approved">⚡ {bankAutomationRate.patternMatched} Muster</span>
                    <span title="KI-Vorschlag akzeptiert">✦ {bankAutomationRate.aiSuggested} KI</span>
                    {bankAutomationRate.needsReview > 0 && (
                      <span className="text-warning-600" title="Ausstehende manuelle Prüfung">⚠ {bankAutomationRate.needsReview} offen</span>
                    )}
                    <span className="text-gray-400">von {bankAutomationRate.total} gesamt</span>
                  </div>
                  <button
                    onClick={() => { void loadBankAutomationRate(); void loadGcReviewItems() }}
                    className="text-caption text-gray-400 hover:text-gray-600"
                    title="Aktualisieren"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                </div>
              ) : null}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* GoCardless — Offene Buchungspruefungen (SAP-Abgleich + KI-Vorschlaege) */}
      {(gcReviewLoading || gcReviewItems.length > 0) && (
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-brand-600" />
                <span className="font-semibold text-sm">GoCardless — Offene Buchungsprüfungen</span>
                {!gcReviewLoading && gcReviewItems.length > 0 && (
                  <Badge variant="warning" size="sm">{gcReviewItems.length}</Badge>
                )}
              </div>
              <IconButton
                icon={<RefreshCw className="w-3 h-3" />}
                aria-label="Aktualisieren"
                size="xs"
                onClick={() => void loadGcReviewItems()}
              />
            </div>
          </Card.Header>
          <Card.Body>
            {gcReviewLoading ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Lade ausstehende Buchungen...</span>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {gcReviewItems.map((tx) => (
                  <div key={tx.id} className="py-3 flex flex-col gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-caption text-gray-400">{tx.bookingDate}</span>
                      <span className={`text-sm font-semibold ${parseFloat(tx.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {parseFloat(tx.amount) >= 0 ? '+' : ''}{parseFloat(tx.amount).toFixed(2)} {tx.currency}
                      </span>
                      <span className="text-sm font-medium text-gray-800">{tx.counterpartyName ?? '(unbekannt)'}</span>
                      {tx.purpose && <span className="text-caption text-gray-400 truncate max-w-xs">{tx.purpose}</span>}
                    </div>
                    {tx.proposal && (
                      <div className="flex items-center gap-3 bg-blue-50 rounded p-2 text-sm flex-wrap">
                        <Receipt className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-blue-800">SAP-Abgleich: </span>
                          <span className="text-blue-700">{tx.proposal.proposedCardName ?? tx.proposal.proposedCardCode ?? '–'}</span>
                          {tx.proposal.proposedDocNum != null && <span className="text-blue-500"> · Dok #{tx.proposal.proposedDocNum}</span>}
                          {tx.proposal.proposedAmount && (
                            <span className="text-blue-500"> · {parseFloat(tx.proposal.proposedAmount).toFixed(2)} €</span>
                          )}
                          <Badge variant="neutral" size="sm" className="ml-2">{tx.proposal.score}%</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            size="xs"
                            onClick={() => void approveGcProposal(tx.id, tx.proposal!.id)}
                            disabled={gcApprovingId === tx.proposal.id}
                          >
                            {gcApprovingId === tx.proposal.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : '✓ Buchen'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => void rejectGcProposal(tx.id, tx.proposal!.id)}
                            disabled={gcApprovingId === tx.proposal.id}
                          >
                            Ablehnen
                          </Button>
                        </div>
                      </div>
                    )}
                    {tx.aiSuggestion && (
                      <div className="flex items-center gap-3 bg-purple-50 rounded p-2 text-sm flex-wrap">
                        <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-purple-800">KI-Vorschlag: </span>
                          {tx.aiSuggestion.suggestedGlAccount && (
                            <span className="text-purple-700">GL {tx.aiSuggestion.suggestedGlAccount}</span>
                          )}
                          {tx.aiSuggestion.suggestedBpCode && (
                            <span className="text-purple-500"> · BP {tx.aiSuggestion.suggestedBpCode}</span>
                          )}
                          {tx.aiSuggestion.suggestedDescription && (
                            <span className="text-gray-600"> · {tx.aiSuggestion.suggestedDescription}</span>
                          )}
                          <Badge
                            variant={tx.aiSuggestion.confidence >= 80 ? 'success' : tx.aiSuggestion.confidence >= 50 ? 'warning' : 'danger'}
                            size="sm"
                            className="ml-2"
                          >
                            {tx.aiSuggestion.confidence}%
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="accent"
                            size="xs"
                            onClick={() => void acceptGcAiSuggestion(tx.id, tx.aiSuggestion!.id)}
                            disabled={gcApprovingId === tx.aiSuggestion.id}
                          >
                            {gcApprovingId === tx.aiSuggestion.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : '✓ Lernen & Akzeptieren'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => void rejectGcAiSuggestion(tx.id, tx.aiSuggestion!.id)}
                            disabled={gcApprovingId === tx.aiSuggestion.id}
                          >
                            Ablehnen
                          </Button>
                        </div>
                      </div>
                    )}
                    {!tx.proposal && !tx.aiSuggestion && (
                      <div className="text-caption text-gray-400 pl-1">Kein Vorschlag verfügbar — KI-Analyse ausstehend</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <DataTable<CrossTenantStatement>
          data={filtered}
          getRowId={(s) => rowKey(s)}
          enableSorting={false}
          expandedRowIds={Array.from(expandedRows)}
          onExpandedChange={(id, expanded) => {
            const s = filtered.find((r) => rowKey(r) === id)
            if (s && expanded !== expandedRows.has(id)) toggleExpand(s)
          }}
          getRowCanExpand={() => true}
          renderExpandedRow={({ original: s }) => {
            const k = rowKey(s)
            const detail = rowDetails[k]
            const isLoadingDetail = loadingDetail.has(k)
            const status = getStatus(s)
            return (
              <>
                {isLoadingDetail && !detail && (
                  <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-gray-400" /></div>
                )}
                <StatementRowsTable
                  statement={s}
                  rows={((detail?.BankStatementRows ?? s.BankStatementRows ?? []) as SapBankStatementRow[])}
                  editingCell={editingCell}
                  setEditingCell={setEditingCell}
                  onSave={saveCellEdit}
                  isFinalized={status !== 'bssDraft'}
                  internalCodes={internalCodes[s.tenantId] ?? []}
                  externalCodes={externalCodes[s.tenantId] ?? []}
                  openInvoices={openInvoicesByTenant[s.tenantId] ?? []}
                  bookingInProgress={bookingInProgress}
                  onBookRow={bookRowInline}
                  searchQuery={searchQuery}
                  isCached={cachedStmtKeys.has(k)}
                  onRefreshInternCodes={refreshInternCodesForTenant}
                  onRefreshExternCodes={refreshExternCodesForTenant}
                  onCreatePattern={(prefill) => { setCreatePatternPrefill(prefill); setShowCreatePatternDialog(true) }}
                />
              </>
            )
          }}
          columns={[
            {
              accessorKey: 'tenantCode',
              header: 'Mandant',
              cell: ({ row }) => (
                <div className="flex justify-center">
                  <Badge variant="neutral" size="sm">{row.original.tenantCode}</Badge>
                </div>
              ),
            },
            {
              id: 'bank',
              header: 'Bank',
              cell: ({ row }) => <span className="text-gray-700">{bankLabel(row.original)}</span>,
            },
            {
              id: 'statementNo',
              header: 'Auszug-Nr.',
              cell: ({ row }) => {
                const ae = getAbsEntry(row.original)
                return (
                  <span className="text-gray-700 font-mono text-xs">
                    {getStatementNo(row.original) || (ae != null ? `#${ae}` : '–')}
                  </span>
                )
              },
            },
            {
              id: 'statementDate',
              header: 'Datum',
              cell: ({ row }) => <span className="text-gray-700 whitespace-nowrap">{getStatementDate(row.original)}</span>,
            },
            {
              id: 'rowCount',
              header: 'Buchungen',
              cell: ({ row }) => (
                <span className="block text-right text-gray-600 whitespace-nowrap tabular-nums">
                  {getRowCount(row.original) ?? '–'}
                </span>
              ),
            },
            {
              id: 'startBalance',
              header: 'Anfangssaldo',
              cell: ({ row }) => (
                <span className="block text-right text-gray-700 whitespace-nowrap tabular-nums">
                  {formatMoney(getStartBalance(row.original), getCurrency(row.original))}
                </span>
              ),
            },
            {
              id: 'endBalance',
              header: 'Endsaldo',
              cell: ({ row }) => (
                <span className="block text-right text-gray-900 font-medium whitespace-nowrap tabular-nums">
                  {formatMoney(getEndBalance(row.original), getCurrency(row.original))}
                </span>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              cell: ({ row }) => <BspStatusBadge status={getStatus(row.original)} />,
            },
            {
              id: '__actions',
              header: 'Aktionen',
              enableSorting: false,
              cell: ({ row }) => {
                const s = row.original
                const ae = getAbsEntry(s)
                const status = getStatus(s)
                if (status !== 'bssDraft' || ae == null) return null
                return (
                  <div className="inline-flex items-center gap-1 justify-end w-full" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => autoMatchBp(s)}
                      disabled={autoMatchInProgress.has(rowKey(s))}
                      loading={autoMatchInProgress.has(rowKey(s))}
                      leadingIcon={<Search className="w-3 h-3" />}
                      title="GP-Codes automatisch zuweisen (eindeutige Treffer)"
                    >
                      GP-Match
                    </Button>
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => autoPostStatement(s)}
                      disabled={autoMatchInProgress.has(rowKey(s))}
                      loading={autoMatchInProgress.has(rowKey(s))}
                      leadingIcon={<Sparkles className="w-3 h-3" />}
                      title="Alle Zeilen automatisch gegen offene Rechnungen buchen"
                    >
                      Auto-Buchen
                    </Button>
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => finalizeStatement(s)}
                      leadingIcon={<Lock className="w-3 h-3" />}
                      title="Auszug fest in SAP buchen"
                    >
                      Fest buchen
                    </Button>
                    <IconButton
                      icon={<Trash2 className="w-3.5 h-3.5" />}
                      variant="ghost"
                      size="xs"
                      aria-label="Auszug löschen"
                      onClick={() => deleteStatement(s)}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      title="Auszug aus SAP löschen (nur Entwürfe)"
                    />
                  </div>
                )
              },
            },
          ]}
        />
      )}

      {showUpload && (
        <StatementUploadDialog
          tenants={tenants.filter((t) => t.isActive)}
          onClose={() => { setShowUpload(false); loadStatements() }}
          onDone={() => { loadStatements() }}
        />
      )}

      {/* ── Buchungsmuster-Analyse Modal ── */}
      <Modal
        open={showPatternAnalysis}
        onOpenChange={(open) => {
          if (!open) { setShowPatternAnalysis(false); setPatternAnalysisResult(null) }
        }}
        size="xl"
        className="max-w-5xl"
        title={
          <span className="flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-accent-500" />
            Buchungsmuster analysieren
          </span>
        }
        description="Analysiert vorhandene SAP-Buchungszeilen und leitet automatisch Muster für Extern-Code, Intern-Code, Sachkonto und GP-Code ab."
      >
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex items-end gap-3 flex-wrap">
            <Select
              label="Mandant"
              value={patternAnalysisTenantId}
              onValueChange={(v) => { setPatternAnalysisTenantId(v); setPatternAnalysisResult(null) }}
              placeholder="– Mandant wählen –"
              wrapperClassName="min-w-[220px]"
              options={tenants.filter((t) => t.isActive).map((t) => ({
                value: t.id,
                label: `${t.code} · ${t.name}`,
              }))}
            />
            <Button
              variant="primary"
              leadingIcon={patternAnalysisLoading ? <Loader2 className="animate-spin" /> : <BarChart2 />}
              disabled={!patternAnalysisTenantId || patternAnalysisLoading}
              onClick={runPatternAnalysis}
            >
              {patternAnalysisLoading ? 'Analysiere…' : 'Analysieren'}
            </Button>
            {patternAnalysisResult && (
              <span className="text-small text-gray-500">
                {patternAnalysisResult.totalRows} Zeilen analysiert ·{' '}
                {patternAnalysisResult.suggestions.length} Empfänger erkannt
                {patternAnalysisResult.errors.length > 0 && (
                  <span className="text-amber-600 ml-2">{patternAnalysisResult.errors.length} Fehler</span>
                )}
              </span>
            )}
          </div>

          {patternAnalysisError && (
            <ErrorState
              size="sm"
              title="Muster-Analyse fehlgeschlagen"
              description={patternAnalysisError}
            />
          )}

          {/* Ergebnistabelle */}
          {patternAnalysisResult && patternAnalysisResult.suggestions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <Checkbox
                  checked={selectedSuggestions.size === patternAnalysisResult.suggestions.length}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setSelectedSuggestions(new Set(patternAnalysisResult.suggestions.map((s) => s.normalizedName)))
                    } else {
                      setSelectedSuggestions(new Set())
                    }
                  }}
                  label={
                    <span className="text-small text-gray-600">
                      Alle auswählen ({selectedSuggestions.size}/{patternAnalysisResult.suggestions.length})
                    </span>
                  }
                />
                <div className="flex items-center gap-3">
                  {patternSaveResult && (
                    <span className={`text-small ${patternSaveResult.startsWith('Fehler') ? 'text-red-600' : 'text-green-600'}`}>
                      {patternSaveResult}
                    </span>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={patternSaveLoading ? <Loader2 className="animate-spin" /> : <Save />}
                    disabled={selectedSuggestions.size === 0 || patternSaveLoading}
                    onClick={saveSelectedPatterns}
                  >
                    {selectedSuggestions.size} Muster speichern
                  </Button>
                </div>
              </div>

              <DataTable<PatternSuggestion>
                data={patternAnalysisResult.suggestions}
                getRowId={(s) => s.normalizedName}
                enableSorting={false}
                columns={[
                  {
                    id: '__select',
                    header: '',
                    size: 36,
                    enableSorting: false,
                    cell: ({ row }) => {
                      const checked = selectedSuggestions.has(row.original.normalizedName)
                      return (
                        <Checkbox
                          checked={checked}
                          onChange={(e) => {
                            setSelectedSuggestions((prev) => {
                              const next = new Set(prev)
                              if (e.currentTarget.checked) next.add(row.original.normalizedName)
                              else next.delete(row.original.normalizedName)
                              return next
                            })
                          }}
                        />
                      )
                    },
                  },
                  {
                    accessorKey: 'displayName',
                    header: 'Empfänger / Absender',
                    cell: ({ row }) => (
                      <span className="font-medium text-gray-800 max-w-[200px] truncate block" title={row.original.displayName}>
                        {row.original.displayName}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'occurrences',
                    header: 'Buchungen',
                    cell: ({ row }) => (
                      <span className="block text-right text-gray-500 tabular-nums">{row.original.occurrences}</span>
                    ),
                  },
                  {
                    accessorKey: 'externalCode',
                    header: 'Extern-Code',
                    cell: ({ row }) => (
                      <ConfidenceCell value={row.original.externalCode} confidence={row.original.externalCodeConfidence} />
                    ),
                  },
                  {
                    accessorKey: 'internalCode',
                    header: 'Intern-Code',
                    cell: ({ row }) => (
                      <ConfidenceCell value={row.original.internalCode} confidence={row.original.internalCodeConfidence} />
                    ),
                  },
                  {
                    accessorKey: 'glAccount',
                    header: 'Sachkonto',
                    cell: ({ row }) => (
                      <ConfidenceCell value={row.original.glAccount} confidence={row.original.glAccountConfidence} />
                    ),
                  },
                  {
                    accessorKey: 'bpCode',
                    header: 'GP-Code',
                    cell: ({ row }) => (
                      <ConfidenceCell value={row.original.bpCode} confidence={row.original.bpCodeConfidence} />
                    ),
                  },
                ]}
              />

              <p className="mt-3 text-caption text-gray-400">
                Konfidenz = Anteil der Buchungen mit diesem Wert. Grün ≥ 90 %, Gelb 60–89 %, Rot &lt; 60 %.
                Leere Felder bedeuten: Wert in SAP nicht gesetzt oder nicht einheitlich.
              </p>
            </div>
          )}

          {patternAnalysisResult && patternAnalysisResult.suggestions.length === 0 && (
            <EmptyState
              title="Keine Buchungszeilen mit konfigurierten Feldern gefunden."
              description="Stelle sicher, dass die Kontoauszüge des Mandanten in SAP bereits Extern-Code, Intern-Code, Sachkonto oder GP-Code eingetragen haben."
            />
          )}
        </div>
      </Modal>

      {/* ── Muster verwalten Modal ── */}
      <Modal
        open={showPatternsManage}
        onOpenChange={(open) => {
          if (!open) { setShowPatternsManage(false); setPatternEditId(null) }
        }}
        size="xl"
        className="max-w-5xl"
        title={
          <span className="flex items-center gap-2">
            <List className="w-5 h-5 text-accent-500" />
            Buchungsmuster verwalten
          </span>
        }
        description="Gespeicherte Muster für automatische Buchungsvorschläge – bearbeiten oder löschen."
      >
        <div className="space-y-4">
          {/* Mandant-Auswahl */}
          <div className="flex items-center gap-3 flex-wrap">
            <Select
              label="Mandant"
              value={patternsManageTenantId}
              onValueChange={(v) => { setPatternsManageTenantId(v); setPatternEditId(null); loadPatternsManage(v) }}
              wrapperClassName="min-w-[220px]"
              options={tenants.filter((t) => t.isActive).map((t) => ({
                value: t.id,
                label: `${t.code} · ${t.name}`,
              }))}
            />
            {patternsManageLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            <span className="text-caption text-gray-400">{patternsManageList.length} Muster</span>
          </div>

          {/* Tabelle */}
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            {patternsManageList.length === 0 && !patternsManageLoading ? (
              <EmptyState
                title="Keine Muster vorhanden."
                description='Analysiere Buchungen über „Buchungsmuster analysieren".'
              />
            ) : (
              <DataTable<BookingPattern>
                data={patternsManageList}
                getRowId={(p: BookingPattern) => p.id}
                enableSorting={false}
                columns={[
                  {
                    accessorKey: 'matchCounterparty',
                    header: 'Empfänger / Absender',
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      const ev = patternEditValues
                      return isEditing
                        ? <Input value={ev.matchCounterparty ?? p.matchCounterparty ?? ''} onChange={(e) => setPatternEditValues((v) => ({ ...v, matchCounterparty: e.currentTarget.value }))} className="py-1 text-small" />
                        : <span className="font-medium text-gray-800">{p.matchCounterparty ?? '–'}</span>
                    },
                  },
                  {
                    accessorKey: 'matchPurpose',
                    header: 'Verwendungszweck-Match',
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      const ev = patternEditValues
                      return isEditing
                        ? <Input value={ev.matchPurpose ?? p.matchPurpose ?? ''} onChange={(e) => setPatternEditValues((v) => ({ ...v, matchPurpose: e.currentTarget.value || null }))} placeholder="leer = kein Filter" className="py-1 text-small" />
                        : <span className="text-gray-500 italic">{p.matchPurpose ?? '–'}</span>
                    },
                  },
                  {
                    accessorKey: 'proposedGlAccount',
                    header: 'Sachkonto',
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      const ev = patternEditValues
                      return isEditing
                        ? <Input value={ev.proposedGlAccount ?? p.proposedGlAccount ?? ''} onChange={(e) => setPatternEditValues((v) => ({ ...v, proposedGlAccount: e.currentTarget.value || null }))} className="py-1 text-small w-24" />
                        : <span className="font-mono text-blue-700">{p.proposedGlAccount ?? '–'}</span>
                    },
                  },
                  {
                    accessorKey: 'proposedBpCode',
                    header: 'GP-Code',
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      const ev = patternEditValues
                      return isEditing
                        ? <Input value={ev.proposedBpCode ?? p.proposedBpCode ?? ''} onChange={(e) => setPatternEditValues((v) => ({ ...v, proposedBpCode: e.currentTarget.value || null }))} className="py-1 text-small w-28" />
                        : <span className="font-mono text-green-700">{p.proposedBpCode ?? '–'}</span>
                    },
                  },
                  {
                    accessorKey: 'proposedExternalCode',
                    header: 'Extern',
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      const ev = patternEditValues
                      return isEditing
                        ? <Input value={ev.proposedExternalCode ?? p.proposedExternalCode ?? ''} onChange={(e) => setPatternEditValues((v) => ({ ...v, proposedExternalCode: e.currentTarget.value || null }))} className="py-1 text-small w-20" />
                        : <span className="font-mono text-gray-600">{p.proposedExternalCode ?? '–'}</span>
                    },
                  },
                  {
                    accessorKey: 'proposedInternalCode',
                    header: 'Intern',
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      const ev = patternEditValues
                      return isEditing
                        ? <Input value={ev.proposedInternalCode ?? p.proposedInternalCode ?? ''} onChange={(e) => setPatternEditValues((v) => ({ ...v, proposedInternalCode: e.currentTarget.value || null }))} className="py-1 text-small w-20" />
                        : <span className="font-mono text-gray-600">{p.proposedInternalCode ?? '–'}</span>
                    },
                  },
                  {
                    accessorKey: 'useCount',
                    header: 'Treffer',
                    cell: ({ row }) => (
                      <span className="block text-right text-gray-400 tabular-nums">{row.original.useCount}</span>
                    ),
                  },
                  {
                    id: '__actions',
                    header: '',
                    enableSorting: false,
                    cell: ({ row }) => {
                      const p = row.original
                      const isEditing = patternEditId === p.id
                      return (
                        <div className="flex items-center gap-1 justify-end">
                          {isEditing ? (
                            <>
                              <IconButton
                                icon={patternEditSaving ? <Loader2 className="animate-spin" /> : <Check />}
                                size="xs"
                                aria-label="Speichern"
                                className="text-green-600 hover:bg-green-100"
                                disabled={patternEditSaving}
                                onClick={savePatternEdit}
                              />
                              <IconButton
                                icon={<X />}
                                size="xs"
                                aria-label="Abbrechen"
                                onClick={() => { setPatternEditId(null); setPatternEditValues({}) }}
                              />
                            </>
                          ) : (
                            <>
                              <IconButton
                                icon={<Edit2 />}
                                size="xs"
                                aria-label="Bearbeiten"
                                className="text-accent-500 hover:bg-accent-50"
                                onClick={() => { setPatternEditId(p.id); setPatternEditValues({}) }}
                              />
                              <IconButton
                                icon={patternDeleteLoading === p.id ? <Loader2 className="animate-spin" /> : <Trash2 />}
                                size="xs"
                                variant="danger"
                                aria-label="Löschen"
                                disabled={patternDeleteLoading === p.id}
                                onClick={() => deletePattern(p.id)}
                              />
                            </>
                          )}
                        </div>
                      )
                    },
                  },
                ]}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* ── KI-Buchungsvorschlaege Modal ── */}
      <Modal
        open={showAiSuggestions}
        onOpenChange={(open) => { if (!open) setShowAiSuggestions(false) }}
        size="xl"
        className="max-w-6xl"
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-700" />
            KI-Buchungsvorschläge
          </span>
        }
        description="Analysiert ungebuchte Zeilen mit Claude AI und Buchungsmustern – täglich automatisch."
      >
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex items-end gap-3 flex-wrap">
            <Select
              label="Mandant"
              value={aiSuggestionTenantId}
              onValueChange={(v) => {
                setAiSuggestionTenantId(v)
                setAiSuggestions([])
                if (v) loadAiSuggestions(v)
              }}
              placeholder="– Mandant wählen –"
              wrapperClassName="min-w-[220px]"
              options={tenants.filter((t) => t.isActive).map((t) => ({
                value: t.id,
                label: `${t.code} · ${t.name}`,
              }))}
            />

            {/* Daily-Run-Status */}
            <Badge variant={aiRunDue ? 'warning' : 'success'}>
              {aiRunDue ? `Analyse ausstehend (letzter Lauf: ${lastAiRunDate || 'noch nie'})` : 'Heute bereits analysiert'}
            </Badge>
            <Button
              variant="primary"
              leadingIcon={aiAnalysisRunning ? <Loader2 className="animate-spin" /> : <Sparkles />}
              disabled={!aiSuggestionTenantId || aiAnalysisRunning}
              onClick={runAiAnalysis}
            >
              {aiAnalysisRunning ? 'Analysiere…' : 'Analyse starten'}
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<RefreshCw className={aiSuggestionsLoading ? 'animate-spin' : undefined} />}
              disabled={!aiSuggestionTenantId || aiSuggestionsLoading}
              onClick={() => aiSuggestionTenantId && loadAiSuggestions(aiSuggestionTenantId)}
            >
              Laden
            </Button>

            {aiAnalysisProgress && (
              <div className="flex-1 min-w-[200px]">
                <div className="text-caption text-gray-500 mb-1">{aiAnalysisProgress.done} / {aiAnalysisProgress.total} Zeilen analysiert</div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-brand-700 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.round((aiAnalysisProgress.done / aiAnalysisProgress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {aiAnalysisError && (
            <ErrorState
              size="sm"
              title="KI-Analyse fehlgeschlagen"
              description={aiAnalysisError}
            />
          )}

          {/* Zusammenfassung */}
          {aiSuggestions.length > 0 && (() => {
            const counts = { pending: 0, approved: 0, rejected: 0, applied: 0 }
            for (const s of aiSuggestions) counts[s.status]++
            return (
              <div className="flex gap-2 text-caption text-gray-600">
                <Badge variant="neutral" size="sm">{counts.pending} ausstehend</Badge>
                <Badge variant="success" size="sm">{counts.approved} genehmigt</Badge>
                <Badge variant="danger" size="sm">{counts.rejected} abgelehnt</Badge>
                <Badge variant="info" size="sm">{counts.applied} gebucht</Badge>
              </div>
            )
          })()}

          {/* Batch-Approve-Leiste */}
          {selectedAiIds.size > 0 && (
            <div className="flex items-center gap-3 p-2 bg-brand-50 border border-brand-200 rounded">
              <span className="text-small text-brand-700">{selectedAiIds.size} ausgewählt</span>
              <Button variant="primary" size="sm" loading={batchApproving} onClick={batchApproveAiSuggestions}>
                Ausgewählte genehmigen
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedAiIds(new Set())}>Auswahl aufheben</Button>
            </div>
          )}

          {/* Book-now bar: shown when approved SAP suggestions are present */}
          {(() => {
            const approvedCount = aiSuggestions.filter((s) => s.status === 'approved' && !s.transactionId).length
            if (approvedCount === 0) return null
            return (
              <div className="flex items-center gap-3 p-2 bg-green-50 border border-green-200 rounded">
                <span className="text-small text-green-800 font-medium">
                  {approvedCount} Vorschlag{approvedCount !== 1 ? 'e' : ''} genehmigt — bereit zum Buchen
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  loading={batchApplying}
                  onClick={() => void batchApplyAiSuggestions()}
                >
                  Jetzt buchen ({approvedCount})
                </Button>
              </div>
            )
          })()}

          {/* Vorschlagstabelle */}
          {aiSuggestions.length > 0 ? (
            <div className="overflow-x-auto">
              <DataTable<AiSuggestionRecord>
                data={aiSuggestions}
                getRowId={(sug: AiSuggestionRecord) => sug.id}
                enableSorting={false}
                expandedRowIds={Array.from(expandedAiReasoningIds)}
                getRowCanExpand={() => true}
                renderExpandedRow={({ original: sug }) => (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 space-y-1">
                    <div className="flex flex-wrap gap-x-6 gap-y-0.5 text-small">
                      <span className="text-gray-500">Datum: <span className="text-gray-800 font-medium">{sug.runDate}</span></span>
                      <span className="text-gray-500">Gegenseite: <span className="text-gray-800 font-medium">{sug.counterpartyName ?? '–'}</span></span>
                      <span className="text-gray-500">Betrag: <span className={`font-mono font-medium ${parseFloat(sug.amount ?? '0') >= 0 ? 'text-green-700' : 'text-red-700'}`}>{parseFloat(sug.amount ?? '0') >= 0 ? '+' : ''}{parseFloat(sug.amount ?? '0').toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span></span>
                    </div>
                    {sug.purpose && (
                      <div className="text-small text-gray-500">Verwendungszweck: <span className="text-gray-800">{sug.purpose}</span></div>
                    )}
                    {sug.aiReasoning && (
                      <div className="text-small text-gray-500 italic">{sug.matchedPatternId ? '📋 Muster: ' : '✨ KI: '}{sug.aiReasoning}</div>
                    )}
                  </div>
                )}
                columns={[
                  {
                    id: '__select',
                    header: () => (
                      <Checkbox
                        checked={aiSuggestions.filter((s) => s.status === 'pending').length > 0 &&
                          aiSuggestions.filter((s) => s.status === 'pending').every((s) => selectedAiIds.has(s.id))}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedAiIds(new Set(aiSuggestions.filter((s) => s.status === 'pending').map((s) => s.id)))
                          else setSelectedAiIds(new Set())
                        }}
                      />
                    ),
                    cell: ({ row }) => row.original.status === 'pending' ? (
                      <Checkbox
                        checked={selectedAiIds.has(row.original.id)}
                        onChange={(e) => {
                          setSelectedAiIds((prev) => {
                            const next = new Set(prev)
                            if (e.target.checked) next.add(row.original.id); else next.delete(row.original.id)
                            return next
                          })
                        }}
                      />
                    ) : null,
                    enableSorting: false,
                  },
                  {
                    id: 'ref',
                    header: 'Auszug / Zeile',
                    cell: ({ row }) => (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-gray-500">#{row.original.absoluteEntry}.{row.original.lineNum}</span>
                        <Badge variant={row.original.matchedPatternId ? 'info' : 'neutral'} size="sm">
                          {row.original.matchedPatternId ? 'Muster' : `KI ${row.original.confidence}%`}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => setExpandedAiReasoningIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(row.original.id)) next.delete(row.original.id); else next.add(row.original.id)
                            return next
                          })}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {expandedAiReasoningIds.has(row.original.id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      </div>
                    ),
                  },
                  {
                    accessorKey: 'counterpartyName',
                    header: 'Gegenseite',
                    cell: ({ row }) => (
                      <span className="max-w-[160px] truncate block text-gray-800" title={a.name(row.original.counterpartyName ?? '')}>
                        {row.original.counterpartyName ? a.name(row.original.counterpartyName) : <span className="text-gray-400">(unbekannt)</span>}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'purpose',
                    header: 'Verwendungszweck',
                    cell: ({ row }) => (
                      <span className="max-w-[200px] truncate block text-gray-700 text-small" title={row.original.purpose ?? ''}>
                        {row.original.purpose || <span className="text-gray-300">–</span>}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'amount',
                    header: 'Betrag',
                    cell: ({ row }) => {
                      const amtRaw = parseFloat(row.original.amount ?? '0')
                      const amt = a.amount(amtRaw)
                      return (
                        <span className={`block text-right font-mono ${amt >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {amt >= 0 ? '+' : ''}{amt.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </span>
                      )
                    },
                  },
                  {
                    accessorKey: 'suggestedBpCode',
                    header: 'GP-Code',
                    cell: ({ row }) => (
                      <span className="font-mono">{a.code(row.original.suggestedBpCode ?? '') || <span className="text-gray-300">–</span>}</span>
                    ),
                  },
                  {
                    accessorKey: 'suggestedGlAccount',
                    header: 'Sachkonto',
                    cell: ({ row }) => (
                      <span className="font-mono">{row.original.suggestedGlAccount || <span className="text-gray-300">–</span>}</span>
                    ),
                  },
                  {
                    accessorKey: 'suggestedExternalCode',
                    header: 'Extern',
                    cell: ({ row }) => (
                      <span className="font-mono">{row.original.suggestedExternalCode || <span className="text-gray-300">–</span>}</span>
                    ),
                  },
                  {
                    accessorKey: 'status',
                    header: 'Status',
                    cell: ({ row }) => (
                      <div className="flex justify-center">
                        <StatusBadge
                          status={row.original.status}
                          label={row.original.status === 'approved' ? 'Genehmigt' : undefined}
                          size="sm"
                        />
                      </div>
                    ),
                  },
                  {
                    id: '__actions',
                    header: 'Aktionen',
                    enableSorting: false,
                    cell: ({ row }) => {
                      const sug = row.original
                      if (sug.status === 'pending') {
                        return (
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="primary" size="xs" aria-label="Vorschlag übernehmen" onClick={() => approveAiSuggestion(sug.id)}>✓</Button>
                            <Button variant="danger" size="xs" aria-label="Vorschlag ablehnen" onClick={() => rejectAiSuggestion(sug.id)}>✗</Button>
                          </div>
                        )
                      }
                      if (sug.status === 'approved') {
                        return (
                          <div className="flex justify-center">
                            <Button variant="secondary" size="xs" onClick={() => rejectAiSuggestion(sug.id)}>Rückgängig</Button>
                          </div>
                        )
                      }
                      return null
                    },
                  },
                ]}
              />
              <p className="mt-3 text-caption text-gray-400">
                Muster = Buchungsmuster-Treffer · KI = Claude AI-Analyse
                · Konfidenz ≥ 80 % grün, ≥ 50 % gelb, &lt; 50 % rot.
              </p>
            </div>
          ) : !aiSuggestionsLoading && !aiAnalysisRunning && (
            <EmptyState
              title={
                aiSuggestionTenantId
                  ? 'Noch keine Vorschläge für diesen Mandanten.'
                  : 'Bitte einen Mandanten wählen.'
              }
              description={aiSuggestionTenantId ? 'Starte die Analyse.' : undefined}
              icon={<Sparkles />}
            />
          )}

          {aiSuggestionsLoading && <LoadingState label="Lade Vorschläge…" />}
        </div>
      </Modal>

      {/* Muster anlegen Dialog */}
      <Modal
        open={showCreatePatternDialog}
        onOpenChange={(open) => { if (!open) { setShowCreatePatternDialog(false); setCreatePatternPrefill(null) } }}
        title={
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Buchungsmuster anlegen
          </span>
        }
        description="Erstellt ein neues Muster für künftige automatische Zuordnungen."
      >
        {createPatternPrefill && (
          <div className="space-y-4">
            <Input
              label="Gegenseite (Muster)"
              value={createPatternPrefill.matchCounterparty ?? ''}
              onChange={(e) => setCreatePatternPrefill((p) => ({ ...p, matchCounterparty: e.target.value }))}
              placeholder="z.B. REWE oder exakter Name"
            />
            <Input
              label="Verwendungszweck (Muster)"
              value={createPatternPrefill.matchPurpose ?? ''}
              onChange={(e) => setCreatePatternPrefill((p) => ({ ...p, matchPurpose: e.target.value }))}
              placeholder="z.B. Rechnung 2024"
            />
            <Input
              label="Sachkonto"
              value={createPatternPrefill.proposedGlAccount ?? ''}
              onChange={(e) => setCreatePatternPrefill((p) => ({ ...p, proposedGlAccount: e.target.value }))}
              placeholder="z.B. 7000"
            />
            <Input
              label="GP-Code"
              value={createPatternPrefill.proposedBpCode ?? ''}
              onChange={(e) => setCreatePatternPrefill((p) => ({ ...p, proposedBpCode: e.target.value }))}
              placeholder="z.B. C10000"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => { setShowCreatePatternDialog(false); setCreatePatternPrefill(null) }}>Abbrechen</Button>
              <Button variant="primary" loading={createPatternSaving} onClick={() => saveCreatedPattern(createPatternPrefill)}>
                Muster speichern
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* BookRowDialog entfernt – alles laeuft inline in der Row-Tabelle */}
    </div>
  )
}


/** Zeigt einen Wert mit farbiger Konfidenz-Anzeige (gruen/gelb/rot). */
function ConfidenceCell({ value, confidence }: { value: string | null; confidence: number }) {
  if (!value) return <span className="text-gray-300">–</span>
  const variant = confidence >= 90 ? 'success' : confidence >= 60 ? 'warning' : 'danger'
  return (
    <Badge variant={variant} size="sm" className="font-mono">
      {value}
      <span className="text-[10px] opacity-70">{confidence}%</span>
    </Badge>
  )
}

// ─── Detail-Zeilen einer Statement ───────────────────────────────────────────

function StatementRowsTable({ statement, rows, editingCell, setEditingCell, onSave, isFinalized, internalCodes, externalCodes, openInvoices, bookingInProgress, onBookRow, searchQuery, isCached, onRefreshInternCodes, onRefreshExternCodes, onCreatePattern }: {
  statement: CrossTenantStatement
  rows: SapBankStatementRow[]
  editingCell: { key: string; lineNum: number; field: string; initialQuery?: string } | null
  setEditingCell: (c: { key: string; lineNum: number; field: string; initialQuery?: string } | null) => void
  onSave: (s: CrossTenantStatement, lineNum: number, field: string, value: string, extraPatch?: Record<string, string>) => void
  isFinalized: boolean
  internalCodes: InternalBankOpCode[]
  externalCodes: InternalBankOpCode[]
  openInvoices: OpenInvoiceLite[]
  bookingInProgress: Set<string>
  onBookRow: (s: CrossTenantStatement, row: SapBankStatementRow, params?: { invoice?: OpenInvoiceLite; cashDiscount?: number }) => Promise<void>
  searchQuery: string
  isCached?: boolean
  onRefreshInternCodes?: (tenantId: string) => Promise<void>
  onRefreshExternCodes?: (tenantId: string) => Promise<void>
  onCreatePattern?: (prefill: Partial<BookingPattern>) => void
}) {
  const a = useAnonymize()
  const formatMoney = a.fmtMoney
  const [historyForLine, setHistoryForLine] = useState<number | null>(null)
  const [historyData, setHistoryData] = useState<HistoryRow[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  async function loadHistory(lineNum: number, counterpartyName: string) {
    if (historyForLine === lineNum) { setHistoryForLine(null); return }
    setHistoryForLine(lineNum)
    setHistoryData([])
    setHistoryLoading(true)
    try {
      const ae = getAbsEntry(statement) ?? 0
      const res = await apiClient.get<HistoryRow[]>('/buchhaltung/sap/row-history', {
        params: { tenantId: statement.tenantId, counterpartyName, excludeAbsEntry: ae, limit: 10 },
      })
      setHistoryData(res.data)
    } catch { setHistoryData([]) } finally { setHistoryLoading(false) }
  }

  function applyHistory(lineNum: number, h: HistoryRow) {
    if (h.gl_account) onSave(statement, lineNum, 'AccountNumber', h.gl_account)
    if (h.bp_code) onSave(statement, lineNum, 'BPCode', h.bp_code)
    if (h.external_code) onSave(statement, lineNum, 'ExternalCode', h.external_code)
    if (h.internal_bank_op_code != null) onSave(statement, lineNum, 'InternalBankOpCode', String(h.internal_bank_op_code))
  }
  if (rows.length === 0) {
    return <div className="p-4 text-xs text-gray-500 italic text-center">Keine Buchungen in diesem Auszug</div>
  }
  // Aktuellste Buchung oben
  const sortedRows = [...rows].sort((a, b) => {
    const da = getRowField(a, 'PostingDate', 'DueDate', 'Date')
    const db = getRowField(b, 'PostingDate', 'DueDate', 'Date')
    return db.localeCompare(da)
  })
  const k = `${statement.tenantId}-${getAbsEntry(statement) ?? 'x'}`
  const q = searchQuery.trim().toLowerCase()

  function matchesSearch(r: SapBankStatementRow): boolean {
    if (!q) return false
    const cpName = getRowField(r, 'BPName', 'CustomerSupplierName', 'CardName', 'PartyName', 'AccountName')
    const details = getRowField(r, 'Details', 'Details2', 'Description', 'Memo', 'Remarks')
    const externCode = getRowField(r, 'ExternalCode', 'ExternCode', 'ExternalBankStatementOperationCode')
    const debit = getRowNumber(r, 'DebitAmountLC', 'DebitAmountFC', 'Debit', 'DebitAmount') ?? 0
    const credit = getRowNumber(r, 'CreditAmountLC', 'CreditAmountFC', 'Credit', 'CreditAmount') ?? 0
    return cpName.toLowerCase().includes(q) || details.toLowerCase().includes(q) || externCode.toLowerCase().includes(q)
      || String(debit).includes(q) || String(credit).includes(q)
  }

  const ae = getAbsEntry(statement) ?? 0
  return (
    <div className="border-t border-gray-200">
      <DataTable<SapBankStatementRow>
        data={sortedRows}
        getRowId={(r) => String(getRowLineNum(r))}
        enableSorting={false}
        rowClassName={(r) => matchesSearch(r) ? 'bg-yellow-50' : undefined}
        expandedRowIds={historyForLine !== null ? [String(historyForLine)] : []}
        renderExpandedRow={({ original: r }) => {
          const lineNum = getRowLineNum(r)
          const cpName = getRowField(r, 'BPName', 'CustomerSupplierName', 'CardName', 'PartyName', 'AccountName')
          const cpNameDisplay = a.name(cpName)
          return (
            <div className="bg-amber-50 -mx-3 -my-2 px-3 py-2">
              <div className="text-[11px] font-medium text-amber-800 mb-1.5 flex items-center gap-1.5">
                <FileText className="w-3 h-3" />
                Vergleichbare Buchungen für „{cpNameDisplay}"
                {!isCached && <span className="text-[10px] text-amber-600 font-normal">(Caching läuft im Hintergrund – bei nächster Aktualisierung vollständig)</span>}
              </div>
              {historyLoading ? (
                <div className="flex items-center gap-1.5 text-amber-600"><Loader2 className="w-3 h-3 animate-spin" /> Lade…</div>
              ) : historyData.length === 0 ? (
                <div className="text-amber-600 italic">Keine historischen Buchungen gefunden.</div>
              ) : (
                <DataTable<HistoryRow>
                  data={historyData}
                  getRowId={(_h: HistoryRow, i: number) => String(i)}
                  enableSorting={false}
                  columns={[
                    {
                      accessorKey: 'statement_date',
                      header: 'Datum',
                      cell: ({ row }) => (
                        <span className="text-gray-500 whitespace-nowrap">{row.original.statement_date ?? row.original.booking_date ?? '–'}</span>
                      ),
                    },
                    {
                      accessorKey: 'purpose',
                      header: 'Verwendungszweck',
                      cell: ({ row }) => (
                        <span className="text-gray-600 max-w-[200px] truncate block" title={a.freeText(row.original.purpose ?? '')}>
                          {a.freeText(row.original.purpose ?? '').slice(0, 60) || '–'}
                        </span>
                      ),
                    },
                    {
                      id: 'amount',
                      header: 'Betrag',
                      cell: ({ row }) => {
                        const debit = row.original.amount_debit ? parseFloat(row.original.amount_debit) : 0
                        const credit = row.original.amount_credit ? parseFloat(row.original.amount_credit) : 0
                        return (
                          <span className="block text-right whitespace-nowrap tabular-nums">
                            {debit > 0 ? <span className="text-red-600">−{formatMoney(debit)}</span> : credit > 0 ? <span className="text-green-600">+{formatMoney(credit)}</span> : '–'}
                          </span>
                        )
                      },
                    },
                    {
                      accessorKey: 'gl_account',
                      header: 'Sachkonto',
                      cell: ({ row }) => <span className="font-mono text-blue-700">{row.original.gl_account ?? '–'}</span>,
                    },
                    {
                      accessorKey: 'bp_code',
                      header: 'GP-Code',
                      cell: ({ row }) => <span className="font-mono text-green-700">{a.code(row.original.bp_code ?? '') || '–'}</span>,
                    },
                    {
                      id: 'external',
                      header: 'Extern',
                      cell: ({ row }) => {
                        const externLabel = row.original.external_code
                          ? (externalCodes.find((c) => String(c.Code) === row.original.external_code)?.Description ?? row.original.external_code)
                          : '–'
                        return <span className="text-gray-500">{externLabel}</span>
                      },
                    },
                    {
                      id: 'internal',
                      header: 'Intern',
                      cell: ({ row }) => {
                        const rawCode = row.original.internal_bank_op_code
                        // SAP returns 0 for "no code set" — treat as empty
                        const internLabel = rawCode != null && rawCode !== 0
                          ? (internalCodes.find((c) => String(c.Code) === String(rawCode))?.Description ?? String(rawCode))
                          : '–'
                        return <span className="text-gray-500">{internLabel}</span>
                      },
                    },
                    ...(isFinalized ? [] : [{
                      id: '__apply',
                      header: '',
                      enableSorting: false as const,
                      cell: ({ row }: { row: { original: HistoryRow } }) => (
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => applyHistory(lineNum, row.original)}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          title="Werte auf aktuelle Zeile übernehmen"
                        >
                          Übernehmen
                        </Button>
                      ),
                    }]),
                  ]}
                />
              )}
            </div>
          )
        }}
        columns={[
          {
            id: 'date',
            header: 'Datum',
            cell: ({ row }) => (
              <span className="text-gray-600 whitespace-nowrap">
                {getRowField(row.original, 'BankStmtLineDate', 'PostingDate', 'Date').slice(0, 10)}
              </span>
            ),
          },
          {
            id: 'dueDate',
            header: 'Fälligkeit',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const dueDate = getRowField(row.original, 'DueDate', 'BankStmtDueDate').slice(0, 10)
              return (
                <EditableCell
                  value={dueDate}
                  editing={!isFinalized && editingCell?.key === k && editingCell?.lineNum === lineNum && editingCell?.field === 'DueDate'}
                  onStartEdit={() => !isFinalized && setEditingCell({ key: k, lineNum, field: 'DueDate' })}
                  onCancel={() => setEditingCell(null)}
                  onSave={(v) => onSave(statement, lineNum, 'DueDate', v)}
                />
              )
            },
          },
          {
            id: 'counterparty',
            header: 'Empfänger / Absender',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const cpName = getRowField(row.original, 'BPName', 'CustomerSupplierName', 'CardName', 'PartyName', 'AccountName')
              return (
                <EditableCell
                  value={a.name(cpName)}
                  editing={!isFinalized && editingCell?.key === k && editingCell?.lineNum === lineNum && editingCell?.field === 'BPName'}
                  onStartEdit={() => !isFinalized && setEditingCell({ key: k, lineNum, field: 'BPName' })}
                  onCancel={() => setEditingCell(null)}
                  onSave={(v) => onSave(statement, lineNum, 'BPName', v)}
                />
              )
            },
          },
          {
            id: 'details',
            header: 'Verwendungszweck',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const details = getRowField(row.original, 'Details', 'Details2', 'Description', 'Memo', 'Remarks')
              return (
                <EditableCell
                  value={a.freeText(details)}
                  editing={!isFinalized && editingCell?.key === k && editingCell?.lineNum === lineNum && editingCell?.field === 'Details'}
                  onStartEdit={() => !isFinalized && setEditingCell({ key: k, lineNum, field: 'Details' })}
                  onCancel={() => setEditingCell(null)}
                  onSave={(v) => onSave(statement, lineNum, 'Details', v)}
                />
              )
            },
          },
          {
            id: 'externCode',
            header: 'Extern',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const externCode = getRowField(row.original, 'ExternalCode', 'ExternCode', 'ExternalBankStatementOperationCode')
              if (isFinalized) {
                return <span className="text-gray-500 text-[11px] whitespace-nowrap">{externalCodes.find((c) => String(c.Code) === externCode)?.Description ?? externCode ?? '–'}</span>
              }
              return (
                <select
                  value={externCode}
                  onChange={(e) => onSave(statement, lineNum, 'ExternalCode', e.target.value)}
                  className="px-1 py-0.5 border border-gray-200 rounded text-[11px] bg-white max-w-[140px]"
                  title={externalCodes.length === 0 ? 'Keine Extern-Codes aus SAP geladen' : ''}
                >
                  <option value="">–</option>
                  {externCode && !externalCodes.some((c) => String(c.Code) === externCode) && (
                    <option key={externCode} value={externCode}>{externCode}</option>
                  )}
                  {externalCodes.map((c) => (
                    <option key={String(c.Code)} value={String(c.Code)}>
                      {String(c.Code)} {c.Description ? `· ${c.Description}` : ''}
                    </option>
                  ))}
                </select>
              )
            },
          },
          {
            id: 'internCode',
            header: 'Intern',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const internCodeRaw = getRowField(row.original, 'InternalBankOpCode', 'InOpCode', 'InternalBankOperationCode')
              // SAP returns 0 (as string "0") for "no code set" — normalize to empty
              const internCode = internCodeRaw === '0' ? '' : internCodeRaw
              if (isFinalized) {
                const desc = internalCodes.find((c) => String(c.Code) === internCode)?.Description
                return <span className="text-gray-500 text-[11px] whitespace-nowrap">{desc || (internCode || '–')}</span>
              }
              return (
                <select
                  value={internCode}
                  onChange={(e) => onSave(statement, lineNum, 'InternalBankOpCode', e.target.value)}
                  className="px-1 py-0.5 border border-gray-200 rounded text-[11px] bg-white max-w-[140px]"
                  title={internalCodes.length === 0 ? 'Keine Intern-Codes aus SAP geladen' : ''}
                >
                  <option value="">–</option>
                  {internCode && !internalCodes.some((c) => String(c.Code) === internCode) && (
                    <option key={internCode} value={internCode}>{internCode}</option>
                  )}
                  {internalCodes.map((c) => (
                    <option key={String(c.Code)} value={String(c.Code)}>
                      {c.Description || `Code ${c.Code}`}
                    </option>
                  ))}
                </select>
              )
            },
          },
          {
            id: 'debit',
            header: 'Belastung',
            cell: ({ row }) => {
              const debit = getRowNumber(row.original, 'DebitAmountLC', 'DebitAmountFC', 'Debit', 'DebitAmount') ?? 0
              return <span className="block text-right text-red-700 whitespace-nowrap tabular-nums">{debit > 0 ? formatMoney(debit) : ''}</span>
            },
          },
          {
            id: 'credit',
            header: 'Gutschrift',
            cell: ({ row }) => {
              const credit = getRowNumber(row.original, 'CreditAmountLC', 'CreditAmountFC', 'Credit', 'CreditAmount') ?? 0
              return <span className="block text-right text-green-700 whitespace-nowrap tabular-nums">{credit > 0 ? formatMoney(credit) : ''}</span>
            },
          },
          {
            id: 'glAccount',
            header: 'Sachkonto',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const glAccount = getRowField(row.original, 'AccountNumber')
              return (
                <EditableCell
                  value={glAccount}
                  editing={!isFinalized && editingCell?.key === k && editingCell?.lineNum === lineNum && editingCell?.field === 'AccountNumber'}
                  onStartEdit={() => !isFinalized && setEditingCell({ key: k, lineNum, field: 'AccountNumber' })}
                  onCancel={() => setEditingCell(null)}
                  onSave={(v) => onSave(statement, lineNum, 'AccountNumber', v)}
                />
              )
            },
          },
          {
            id: 'bpCode',
            header: 'GP-Code',
            cell: ({ row }) => {
              const lineNum = getRowLineNum(row.original)
              const bpCode = getRowField(row.original, 'BPCode', 'CardCode', 'CustomerSupplierNo')
              const bpCodeDisplay = a.code(bpCode)
              const alreadyBooked = isRowAlreadyBooked(row.original)
              const cpName = getRowField(row.original, 'BPName', 'CustomerSupplierName', 'CardName', 'PartyName', 'AccountName')
              const cpNameDisplay = a.name(cpName)
              return (
                <div className="flex items-center gap-0.5 whitespace-nowrap">
                  {editingCell?.key === k && editingCell?.lineNum === lineNum && editingCell?.field === 'BPCode' ? (
                    <BpSearchCell
                      value={bpCode}
                      tenantId={statement.tenantId}
                      editing={true}
                      onStartEdit={() => {}}
                      onCancel={() => setEditingCell(null)}
                      onSave={(code) => onSave(statement, lineNum, 'BPCode', code)}
                      renderAsTd={false}
                      initialQuery={editingCell?.initialQuery}
                    />
                  ) : (
                    <span
                      onDoubleClick={() => !isFinalized && setEditingCell({ key: k, lineNum, field: 'BPCode' })}
                      className="text-gray-600 text-xs cursor-text truncate max-w-[80px]"
                      title={bpCodeDisplay || '(doppelklick zum Bearbeiten)'}
                    >
                      {bpCodeDisplay || <span className="text-gray-300 italic">–</span>}
                    </span>
                  )}
                  {!isFinalized && !alreadyBooked && !bpCode && cpName && (
                    <IconButton
                      icon={<Search />}
                      aria-label={`GP suchen für "${cpNameDisplay}"`}
                      size="xs"
                      variant="ghost"
                      onClick={() => setEditingCell({ key: k, lineNum, field: 'BPCode', initialQuery: cpName })}
                      className="text-purple-500 hover:text-purple-700 hover:bg-transparent"
                      title={`GP suchen für "${cpNameDisplay}"`}
                    />
                  )}
                </div>
              )
            },
          },
          {
            id: '__action',
            header: 'Aktion',
            enableSorting: false,
            cell: ({ row }) => {
              const r = row.original
              const lineNum = getRowLineNum(r)
              const rowCleared = getRowField(r, 'RowStatus', 'Cleared') === 'bsprs_Reconciled'
              const alreadyBooked = isRowAlreadyBooked(r)
              const cpName = getRowField(r, 'BPName', 'CustomerSupplierName', 'CardName', 'PartyName', 'AccountName')
              const bpCode = getRowField(r, 'BPCode', 'CardCode', 'CustomerSupplierNo')
              const bpCodeDisplay = a.code(bpCode)
              const glAccount = getRowField(r, 'AccountNumber')
              const details = getRowField(r, 'Details', 'Details2', 'Description', 'Memo', 'Remarks')
              const rowDate = getRowField(r, 'BankStmtLineDate', 'PostingDate', 'DueDate').slice(0, 10)
              const debit = getRowNumber(r, 'DebitAmountLC', 'DebitAmountFC', 'Debit', 'DebitAmount') ?? 0
              const credit = getRowNumber(r, 'CreditAmountLC', 'CreditAmountFC', 'Credit', 'CreditAmount') ?? 0
              const invoiceCandidates = !alreadyBooked
                ? findMatchingInvoices(openInvoices, credit, debit, cpName, details, rowDate)
                : []
              const topCandidate = invoiceCandidates[0] ?? null
              const rowId = `${statement.tenantId}-${ae}-${lineNum}`
              const isBooking = bookingInProgress.has(rowId)
              return (
                <div className="flex items-center gap-1 justify-end whitespace-nowrap">
                  {cpName && (
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => loadHistory(lineNum, cpName)}
                      leadingIcon={<FileText className="w-3 h-3" />}
                      className={historyForLine === lineNum ? 'bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-100' : 'hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'}
                      title={isCached ? 'Vergleichbare Buchungen aus der Historie' : 'Caching läuft (nach Aktualisierung verfügbar)'}
                    >
                      Historie
                    </Button>
                  )}
                  {rowCleared && (
                    <Tooltip content="In SAP als OK markiert (abgestimmt)">
                      <Badge variant="success" size="sm" className="font-semibold">OK</Badge>
                    </Tooltip>
                  )}
                  {alreadyBooked ? (
                    <Tooltip content={bpCodeDisplay ? `BP ${bpCodeDisplay}` : 'gebucht'}>
                      <Badge variant="success" size="sm">
                        <CheckCircle2 className="w-3 h-3" /> gebucht
                      </Badge>
                    </Tooltip>
                  ) : isRowAssigned(r) ? (
                    <Tooltip content={`${bpCodeDisplay ? `BP ${bpCodeDisplay}` : ''}${glAccount ? ` Kto ${glAccount}` : ''} – "Fest buchen" zum Abschließen`}>
                      <Badge variant="info" size="sm">
                        <CheckCircle2 className="w-3 h-3" /> zugewiesen
                      </Badge>
                    </Tooltip>
                  ) : isFinalized ? (
                    <span className="text-gray-300 text-[11px]">–</span>
                  ) : topCandidate ? (
                    <div className="flex items-center gap-0.5">
                      <Tooltip content={`${topCandidate.invoice.DocType === 'Invoice' ? 'AR-Rechnung (Debitor)' : 'ER-Rechnung (Kreditor)'} #${a.docNum(topCandidate.invoice.DocNum)} · ${a.name(topCandidate.invoice.CardName)} · Konfidenz: ${topCandidate.score}%`}>
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => onBookRow(statement, r, { invoice: topCandidate.invoice })}
                          disabled={isBooking}
                          loading={isBooking}
                          leadingIcon={<Sparkles className="w-3 h-3" />}
                        >
                          → {topCandidate.invoice.DocType === 'Invoice' ? 'AR' : 'ER'} #{a.docNum(topCandidate.invoice.DocNum)}
                        </Button>
                      </Tooltip>
                      <span
                        className={`text-[10px] font-semibold px-1 py-0.5 rounded ${confidenceToneClasses(topCandidate.score)}`}
                        title={`Konfidenz-Score: ${topCandidate.score}%`}
                      >
                        {topCandidate.score}%
                      </span>
                      {invoiceCandidates.length > 1 && (
                        <Dropdown>
                          <DropdownTrigger asChild>
                            <IconButton
                              icon={<ChevronDown className="w-3 h-3" />}
                              aria-label={`${invoiceCandidates.length - 1} weitere Kandidaten`}
                              size="xs"
                              variant="ghost"
                              title={`${invoiceCandidates.length - 1} weitere Treffer`}
                            />
                          </DropdownTrigger>
                          <DropdownContent align="end" className="min-w-[260px]">
                            <DropdownLabel>Weitere Kandidaten ({invoiceCandidates.length - 1})</DropdownLabel>
                            <DropdownSeparator />
                            {invoiceCandidates.slice(1).map((c) => (
                              <DropdownItem
                                key={c.invoice.DocEntry}
                                onClick={() => onBookRow(statement, r, { invoice: c.invoice })}
                                disabled={isBooking}
                              >
                                <span
                                  className={`text-[10px] font-semibold px-1 py-0.5 rounded shrink-0 ${confidenceToneClasses(c.score)}`}
                                >
                                  {c.score}%
                                </span>
                                <span className="truncate">
                                  {c.invoice.DocType === 'Invoice' ? 'AR' : 'ER'} #{a.docNum(c.invoice.DocNum)} · {a.name(c.invoice.CardName).slice(0, 25)}
                                </span>
                              </DropdownItem>
                            ))}
                          </DropdownContent>
                        </Dropdown>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => onBookRow(statement, r)}
                      disabled={isBooking || (!glAccount && !bpCode)}
                      loading={isBooking}
                      leadingIcon={<Receipt className="w-3 h-3" />}
                      title={!glAccount && !bpCode ? 'Sachkonto oder GP-Code angeben' : 'Zeile jetzt buchen'}
                    >
                      Buchen
                    </Button>
                  )}
                  {alreadyBooked && onCreatePattern && (glAccount || bpCode) && (
                    <Button
                      variant="ghost"
                      size="xs"
                      leadingIcon={<Plus className="w-3 h-3" />}
                      title="Buchungsmuster aus dieser Zeile erstellen"
                      onClick={() => {
                        // Derive amount sign so the new pattern only matches the same debit/credit direction
                        const amount = credit > 0 ? credit : -debit
                        onCreatePattern({
                          tenantId: statement.tenantId,
                          matchCounterparty: cpName || undefined,
                          proposedBpCode: bpCode || undefined,
                          proposedGlAccount: glAccount || undefined,
                          matchAmountMin: amount > 0 ? 0.01 : null,
                          matchAmountMax: amount < 0 ? -0.01 : null,
                        })
                      }}
                    >
                      Muster
                    </Button>
                  )}
                </div>
              )
            },
          },
        ]}
      />
      {/* Eingang/Ausgang Footer + Differenz-Check */}
      {(() => {
        let inCount = 0, inSum = 0, outCount = 0, outSum = 0
        for (const r of rows) {
          const d = getRowNumber(r, 'DebitAmountLC', 'DebitAmountFC', 'Debit') ?? 0
          const c = getRowNumber(r, 'CreditAmountLC', 'CreditAmountFC', 'Credit') ?? 0
          if (c > 0) { inCount++; inSum += c }
          if (d > 0) { outCount++; outSum += d }
        }
        const start = getStartBalance(statement) ?? 0
        const end = getEndBalance(statement) ?? 0
        const computed = start + inSum - outSum
        const diff = Math.round((end - computed) * 100) / 100
        const currency = getCurrency(statement)
        return (
          <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 text-[11px] flex flex-wrap items-center gap-x-6 gap-y-1">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Eingang:</span>
              <span className="font-medium text-green-700">{inCount} · {formatMoney(inSum, currency)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Ausgang:</span>
              <span className="font-medium text-red-700">{outCount} · {formatMoney(outSum, currency)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Saldo-Check:</span>
              <span className="text-gray-700">{formatMoney(start, currency)} + {formatMoney(inSum, currency)} − {formatMoney(outSum, currency)} = {formatMoney(computed, currency)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Differenz:</span>
              <span className={diff === 0 ? 'font-medium text-green-700' : 'font-medium text-red-700'}>
                {formatMoney(diff, currency)}
              </span>
            </div>
          </div>
        )
      })()}
      {!isFinalized && (
        <div className="px-3 py-2 text-[11px] text-gray-400 bg-gray-100 border-t border-gray-200">
          Doppelklick auf eine Zelle zum Bearbeiten · Enter speichert · Esc verwirft
        </div>
      )}
      {/* Debug: SAP-Feldnamen und Code-Anzahl */}
      {rows.length > 0 && (
        <details className="px-3 py-1 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-400">
          <summary className="cursor-pointer hover:text-gray-600">Debug: SAP-Felder &amp; Codes</summary>
          <DebugBankOpCodes tenantId={statement.tenantId} rows={rows} externalCodes={externalCodes} internalCodes={internalCodes} onRefreshInternCodes={onRefreshInternCodes} onRefreshExternCodes={onRefreshExternCodes} />
        </details>
      )}
    </div>
  )
}

function DebugBankOpCodes({ tenantId, rows, externalCodes, internalCodes, onRefreshInternCodes, onRefreshExternCodes }: {
  tenantId: string
  rows: SapBankStatementRow[]
  externalCodes: InternalBankOpCode[]
  internalCodes: InternalBankOpCode[]
  onRefreshInternCodes?: (tenantId: string) => Promise<void>
  onRefreshExternCodes?: (tenantId: string) => Promise<void>
}) {
  const [diagnose, setDiagnose] = useState<{ internal: { codes: unknown[]; errors: string[] }; external: { codes: unknown[]; errors: string[] } } | null>(null)
  const [loading, setLoading] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [reloadingExtern, setReloadingExtern] = useState(false)
  const [metaResult, setMetaResult] = useState<{ matchingEntities: string[]; totalEntities: number; error?: string } | null>(null)
  const [metaLoading, setMetaLoading] = useState(false)
  const [metaSearch, setMetaSearch] = useState('bank')
  const [probeResult, setProbeResult] = useState<unknown>(null)
  const [probeLoading, setProbeLoading] = useState(false)
  const runProbe = async () => {
    setProbeLoading(true)
    try {
      const res = await apiClient.get('/buchhaltung/sap/bank-charges-probe', { params: { tenantId } })
      setProbeResult(res.data)
    } catch (e: unknown) {
      const err = e as { response?: { data?: unknown }; message?: string }
      setProbeResult({ error: JSON.stringify(err?.response?.data ?? err?.message) })
    } finally { setProbeLoading(false) }
  }
  const runDiagnose = async () => {
    setLoading(true)
    try {
      const res = await apiClient.get('/buchhaltung/sap/bank-op-codes-diagnose', { params: { tenantId } })
      setDiagnose(res.data)
    } catch (e) {
      setDiagnose({ internal: { codes: [], errors: [(e as Error).message] }, external: { codes: [], errors: [(e as Error).message] } })
    } finally { setLoading(false) }
  }
  const runMetaSearch = async () => {
    setMetaLoading(true)
    try {
      const res = await apiClient.get('/buchhaltung/sap/metadata-entities', { params: { tenantId, search: metaSearch } })
      setMetaResult(res.data)
    } catch (e: unknown) {
      const err = e as { response?: { data?: unknown }; message?: string }
      setMetaResult({ matchingEntities: [], totalEntities: 0, error: JSON.stringify(err?.response?.data ?? err?.message) })
    } finally { setMetaLoading(false) }
  }
  const reloadInternCodes = async () => {
    setReloading(true)
    try { await onRefreshInternCodes?.(tenantId) } finally { setReloading(false) }
  }
  const reloadExternCodes = async () => {
    setReloadingExtern(true)
    try { await onRefreshExternCodes?.(tenantId) } finally { setReloadingExtern(false) }
  }
  return (
    <div className="mt-1 space-y-1 font-mono">
      <div>Extern-Codes geladen: <b>{externalCodes.length}</b> | Intern-Codes geladen: <b>{internalCodes.length}</b></div>
      <div>Erste Zeile – alle Schlüssel: {Object.keys(rows[0]).sort().join(', ')}</div>
      <div>ExternalCode pro Zeile: {rows.map((r, i) => `[${i}]=${JSON.stringify(r.ExternalCode ?? r.ExternCode ?? null)}`).join(', ')}</div>
      <div>InternalBankOpCode pro Zeile: {rows.map((r, i) => `[${i}]=${JSON.stringify(r.InternalBankOpCode ?? r.InOpCode ?? null)}`).join(', ')}</div>

      {/* SAP $metadata Discovery */}
      <div className="pt-1 border-t border-gray-200 mt-1">
        <div className="text-gray-600 font-semibold mb-0.5 text-[10px]">SAP Entity-Namen aus /$metadata lesen:</div>
        <div className="flex items-center gap-1">
          <Input
            type="text"
            value={metaSearch}
            onChange={(e) => setMetaSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runMetaSearch()}
            placeholder="Suche (z.B. bank, intern, code)"
            wrapperClassName="w-40"
            className="px-1 py-0 text-[10px]"
          />
          <Button variant="secondary" size="xs" onClick={runMetaSearch} disabled={metaLoading} loading={metaLoading}>
            {metaLoading ? 'Suche...' : 'Entities suchen'}
          </Button>
        </div>
        {metaResult && (
          <div className="mt-0.5 text-[10px] font-mono">
            {metaResult.error
              ? <div className="text-red-600">✗ {metaResult.error}</div>
              : <>
                  <div className="text-gray-500">Gesamt: {metaResult.totalEntities} Entities | Treffer für "{metaSearch}": {metaResult.matchingEntities.length}</div>
                  {metaResult.matchingEntities.length > 0
                    ? <div className="bg-indigo-50 rounded p-1 mt-0.5 space-y-0.5 max-h-40 overflow-y-auto">
                        {metaResult.matchingEntities.map((e) => <div key={e} className="text-indigo-800">{e}</div>)}
                      </div>
                    : <div className="text-orange-600">Kein Entity mit "{metaSearch}" gefunden</div>
                  }
                </>
            }
          </div>
        )}
      </div>

      <div className="pt-1 flex items-center gap-2 flex-wrap">
        <Button variant="secondary" size="xs" onClick={runProbe} disabled={probeLoading} loading={probeLoading}>
          {probeLoading ? 'Teste...' : 'POST BankChargesAllocationCodesService testen'}
        </Button>
        <Button variant="secondary" size="xs" onClick={runDiagnose} disabled={loading} loading={loading}>
          {loading ? 'Prüfe...' : 'SAP-Entity-Diagnose starten'}
        </Button>
        {onRefreshInternCodes && (
          <Button variant="secondary" size="xs" onClick={reloadInternCodes} disabled={reloading} loading={reloading}>
            {reloading ? 'Lade...' : 'Intern-Codes neu laden (Cache leeren)'}
          </Button>
        )}
        {onRefreshExternCodes && (
          <Button variant="secondary" size="xs" onClick={reloadExternCodes} disabled={reloadingExtern} loading={reloadingExtern}>
            {reloadingExtern ? 'Lade...' : 'Extern-Codes neu laden (Cache leeren)'}
          </Button>
        )}
      </div>
      {!!probeResult && (
        <div className="pt-1 text-[10px] font-mono border-t border-orange-200">
          <div className="font-semibold text-orange-700 mb-0.5">BankChargesAllocationCodes Probe:</div>
          <pre className="bg-orange-50 rounded p-1 overflow-x-auto max-h-80 whitespace-pre-wrap break-all">{JSON.stringify(probeResult, null, 2)}</pre>
        </div>
      )}
      {diagnose && (
        <div className="space-y-1 text-red-600">
          <div><b>Intern-Codes ({diagnose.internal.codes.length} gefunden):</b></div>
          {diagnose.internal.errors.map((e, i) => <div key={i} className="pl-2">✗ {e}</div>)}
          {diagnose.internal.codes.length > 0 && <div className="text-green-600 pl-2">✓ {diagnose.internal.codes.length} Codes geladen</div>}
          <div><b>Extern-Codes ({diagnose.external.codes.length} gefunden):</b></div>
          {diagnose.external.errors.map((e, i) => <div key={i} className="pl-2">✗ {e}</div>)}
          {diagnose.external.codes.length > 0 && <div className="text-green-600 pl-2">✓ {diagnose.external.codes.length} Codes geladen</div>}
        </div>
      )}
    </div>
  )
}

function EditableCell({ value, editing, onStartEdit, onCancel, onSave }: {
  value: string
  editing: boolean
  onStartEdit: () => void
  onCancel: () => void
  onSave: (v: string) => void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => { setDraft(value) }, [value, editing])
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(draft)
            else if (e.key === 'Escape') onCancel()
          }}
          wrapperClassName="flex-1"
          className="px-2 py-0.5 text-xs"
        />
        <IconButton
          icon={<Save />}
          aria-label="Speichern"
          size="xs"
          variant="ghost"
          onClick={() => onSave(draft)}
          className="text-green-600 hover:text-green-800 hover:bg-transparent"
        />
      </div>
    )
  }
  return (
    <span
      onDoubleClick={onStartEdit}
      className="block text-gray-600 max-w-xs truncate cursor-text"
      title={value || '(doppelklick zum Bearbeiten)'}
    >
      {value || <span className="text-gray-300 italic">–</span>}
    </span>
  )
}

// ─── GP-Code Zelle mit SAP Business-Partner-Suche ──────────────────────────

interface BpSearchResult {
  CardCode: string
  CardName: string
  CardType: string
  FederalTaxID?: string | null
}

function BpSearchCell({ value, tenantId, editing, onStartEdit, onCancel, onSave, renderAsTd = true, initialQuery }: {
  value: string
  tenantId: string
  editing: boolean
  onStartEdit: () => void
  onCancel: () => void
  onSave: (code: string, name?: string) => void
  renderAsTd?: boolean
  initialQuery?: string
}) {
  const a = useAnonymize()
  const [draft, setDraft] = useState(value)
  const [results, setResults] = useState<BpSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const initialSearchDone = useRef(false)

  useEffect(() => { setDraft(value); initialSearchDone.current = false }, [value, editing])

  // Beim Oeffnen mit initialQuery: Suchfeld vorausfuellen und sofort suchen
  useEffect(() => {
    if (editing && initialQuery && !initialSearchDone.current) {
      initialSearchDone.current = true
      setDraft(initialQuery)
      ;(async () => {
        setSearching(true)
        try {
          const res = await apiClient.get<BpSearchResult[]>('/buchhaltung/sap/business-partners/search', { params: { tenantId, q: initialQuery } })
          setResults(res.data ?? [])
          setShowDropdown(true)
        } catch { setResults([]) }
        setSearching(false)
      })()
    }
  }, [editing, initialQuery, tenantId])

  // Close dropdown on click outside
  useEffect(() => {
    if (!showDropdown) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showDropdown])

  function handleSearch(q: string) {
    setDraft(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (q.length < 2) { setResults([]); setShowDropdown(false); return }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await apiClient.get<BpSearchResult[]>('/buchhaltung/sap/business-partners/search', { params: { tenantId, q } })
        setResults(res.data ?? [])
        setShowDropdown(true)
      } catch { setResults([]) }
      setSearching(false)
    }, 300)
  }

  function selectBp(bp: BpSearchResult) {
    setDraft(bp.CardCode)
    setShowDropdown(false)
    onSave(bp.CardCode, bp.CardName)
  }

  // Enter/Save: Wenn genau 1 Ergebnis → diesen nehmen.
  // Wenn draft wie ein CardCode aussieht (nur Ziffern) → direkt speichern.
  // Sonst: nichts tun (User soll aus Dropdown waehlen).
  function handleConfirm() {
    setShowDropdown(false)
    if (results.length === 1) {
      selectBp(results[0]!)
      return
    }
    // Pruefe ob draft ein exakter CardCode aus den Ergebnissen ist
    const exact = results.find((bp) => bp.CardCode === draft)
    if (exact) {
      selectBp(exact)
      return
    }
    // Draft ist ein kurzer Code (z.B. manuell eingetippt) → direkt speichern
    if (draft.length <= 15 && /^[\dA-Z_-]+$/i.test(draft) && draft.length >= 1) {
      onSave(draft)
      return
    }
    // Sonst: Suche offen lassen, User muss aus Dropdown waehlen
    if (results.length > 1) {
      setShowDropdown(true)
    }
  }

  const inner = (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleConfirm()
            else if (e.key === 'Escape') { setShowDropdown(false); onCancel() }
          }}
          placeholder="Code oder Name..."
          wrapperClassName="flex-1 min-w-[140px]"
          className="px-2 py-0.5 text-xs"
        />
        {searching && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
        <IconButton
          icon={<Save />}
          aria-label="Speichern"
          size="xs"
          variant="ghost"
          onClick={handleConfirm}
          className="text-green-600 hover:text-green-800 hover:bg-transparent"
        />
      </div>
      {showDropdown && results.length > 0 && (
        <div className="absolute z-popover top-full left-0 mt-1 w-[320px] max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-popover text-xs">
          {results.map((bp) => (
            <Button
              key={bp.CardCode}
              variant="ghost"
              size="xs"
              fullWidth
              onClick={() => selectBp(bp)}
              className="justify-start rounded-none px-3 py-1.5 text-xs hover:bg-brand-50 border-b border-gray-50 last:border-0"
            >
              <span className="font-mono text-brand-700 shrink-0">{a.code(bp.CardCode)}</span>
              <span className="truncate text-gray-700">{a.name(bp.CardName)}</span>
              <span className="ml-auto text-gray-400 text-[10px] shrink-0">{bp.CardType === 'cCustomer' ? 'Kunde' : 'Lieferant'}</span>
            </Button>
          ))}
        </div>
      )}
      {showDropdown && results.length === 0 && !searching && draft.length >= 2 && (
        <div className="absolute z-popover top-full left-0 mt-1 w-[220px] bg-white border border-gray-200 rounded-md shadow-popover text-xs p-2 text-gray-400 italic">
          Kein GP gefunden
        </div>
      )}
    </div>
  )

  if (editing) {
    return renderAsTd ? <td className="px-2 py-1 relative">{inner}</td> : inner
  }
  if (!renderAsTd) return null
  return (
    <td onDoubleClick={onStartEdit} className="px-3 py-1.5 text-gray-600 max-w-xs truncate cursor-text" title={value || '(doppelklick zum Bearbeiten)'}>
      {value || <span className="text-gray-300 italic">–</span>}
    </td>
  )
}

function BspStatusBadge({ status }: { status: SapBankStatement['Status'] }) {
  return <StatusBadge status={status ?? undefined} size="sm" />
}

/**
 * Hinweis: `formatMoney` wird ueber `useAnonymize().fmtMoney` innerhalb der
 * Komponenten bereitgestellt, damit der Praesentations-/Demo-Modus Betraege
 * deterministisch anonymisieren kann. Call-Sites nutzen unveraendert
 * `formatMoney(amount, currency)`.
 */

// ─── Buchungs-Dialog fuer eine einzelne Statement-Zeile ──────────────────────

interface OpenInvoiceLite {
  DocEntry: number
  DocNum: number
  CardCode: string
  CardName: string
  DocTotal: number
  PaidToDate?: number
  DocCurrency: string
  DocDate: string
  DocDueDate?: string
  DocType: 'Invoice' | 'PurchaseInvoice'
}

interface InvoiceCandidate {
  invoice: OpenInvoiceLite
  /** Confidence score 0–100. Amount+type match = 50 base; name/docnum/date add up to 50 more. */
  score: number
}

/**
 * Returns ranked invoice candidates for a bank statement row.
 * Direction: credit > 0 → AR Invoice (Debitor); debit > 0 → AP PurchaseInvoice (Kreditor).
 * Tolerance: ±0.01 EUR or ±1% of amount (whichever is larger) to handle minor rounding.
 */
function findMatchingInvoices(
  invoices: OpenInvoiceLite[],
  credit: number,
  debit: number,
  cpName: string,
  details: string,
  rowDate: string,
): InvoiceCandidate[] {
  const isIncoming = credit > 0
  const amount = isIncoming ? credit : debit
  if (amount <= 0) return []
  const expectedType: OpenInvoiceLite['DocType'] = isIncoming ? 'Invoice' : 'PurchaseInvoice'
  const tolerance = Math.max(0.01, amount * 0.01)
  const searchText = `${cpName} ${details}`.toLowerCase()

  const candidates: InvoiceCandidate[] = []
  for (const inv of invoices) {
    if (inv.DocType !== expectedType) continue
    // Use open balance (DocTotal minus already paid), falling back to DocTotal.
    const openBalance = inv.PaidToDate != null ? inv.DocTotal - inv.PaidToDate : inv.DocTotal
    if (Math.abs(openBalance - amount) > tolerance) continue

    let score = 50 // base: type + amount matched
    const invName = (inv.CardName ?? '').toLowerCase()

    // Name similarity
    if (invName && (searchText.includes(invName) || invName.includes(cpName.toLowerCase()))) {
      score += 30
    }

    // DocNum or DocEntry found in description/purpose
    if (details && (details.includes(String(inv.DocNum)) || details.includes(String(inv.DocEntry)))) {
      score += 15
    }

    // Date proximity (within 7 days)
    if (rowDate && inv.DocDate) {
      const diffDays = Math.abs(new Date(rowDate).getTime() - new Date(inv.DocDate).getTime()) / 86_400_000
      if (diffDays <= 7) score += 5
    }

    candidates.push({ invoice: inv, score: Math.min(score, 100) })
  }

  return candidates.sort((a, b) => b.score - a.score)
}


// ─── Kontoauszug-Upload direkt aus der Kontoauszugsverarbeitung ─────────────

interface UploadResult {
  filename: string
  format: string
  detectedIban: string | null
  detectedBankName?: string
  transactions: number
  tenantId?: string
  tenantCode?: string
  status: 'created' | 'duplicate' | 'no-iban' | 'unknown-account' | 'error'
  sapAbsoluteEntry?: number
  sapStatementNumber?: string
  message?: string
}

function StatementUploadDialog({ tenants, onClose, onDone }: {
  tenants: Array<{ id: string; code: string; name: string }>
  onClose: () => void
  onDone: () => void
}) {
  const a = useAnonymize()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<UploadResult[] | null>(null)
  const [totals, setTotals] = useState<{ files: number; created: number; duplicates: number; unknownAccount: number; errors: number } | null>(null)
  const [tenantDiagnostics, setTenantDiagnostics] = useState<Array<{ tenantCode: string; tenantName: string; loaded: number; error: string | null }> | null>(null)
  // Datei-Inhalte-Cache (fuer Re-Import nach Konto-Anlage)
  const [contentCache, setContentCache] = useState<Record<string, string>>({})
  // Sub-Dialog (Konto anlegen oder Konto zuordnen) fuer eine Ergebnis-Zeile
  const [subDialog, setSubDialog] = useState<
    | { type: 'create-account'; idx: number; iban: string }
    | { type: 'select-account'; idx: number }
    | null
  >(null)
  // Auto-Post Status pro Ergebnis-Zeile
  const [autoPostStatus, setAutoPostStatus] = useState<Record<number, { loading?: boolean; result?: { booked: number; skipped: number; errors: number; actionable?: number; matchRate?: number; skippedDetails?: Array<{ lineNum: number; reason: string }> } | null }>>({})


  // Hausbankkonten-Cache fuer manuelle Zuordnung (CSV)
  const [houseBankAccounts, setHouseBankAccounts] = useState<Record<string, Array<Record<string, unknown>>>>({})
  const [loadingHba, setLoadingHba] = useState(false)

  function addFiles(list: FileList | null) {
    if (!list) return
    setFiles((prev) => [...prev, ...Array.from(list)].slice(0, 50))
  }
  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  async function upload() {
    if (files.length === 0) return
    setUploading(true)
    setResults(null)
    try {
      const payload = await Promise.all(files.map(async (f) => ({ filename: f.name, content: await f.text() })))
      // Cache fuer spaeteren Re-Import nach Konto-Anlage
      const cache: Record<string, string> = {}
      for (const p of payload) cache[p.filename] = p.content
      setContentCache(cache)
      // Multi-Tenant: keine tenantId → Server probiert automatisch alle
      const res = await apiClient.post('/buchhaltung/sap/import-batch-multi', { files: payload })
      setResults(res.data.results)
      setTotals(res.data.totals)
      setTenantDiagnostics(res.data.tenantDiagnostics ?? null)
      onDone()
    } catch (e: unknown) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      toast({
        title: 'Upload fehlgeschlagen',
        description: error?.response?.data?.error || error?.message || 'Unbekannter Fehler',
        tone: 'danger',
      })
    } finally {
      setUploading(false)
    }
  }

  /**
   * Nach Anlage eines neuen HouseBankAccount die urspruengliche Datei erneut importieren
   * und die Ergebniszeile aktualisieren.
   */
  async function retryAfterAccountCreation(idx: number) {
    if (!results) return
    const row = results[idx]!
    const content = contentCache[row.filename]
    if (!content) {
      toast({
        title: 'Re-Import fehlgeschlagen',
        description: 'Dateiinhalt nicht mehr im Cache – bitte erneut hochladen',
        tone: 'danger',
      })
      return
    }
    try {
      const res = await apiClient.post('/buchhaltung/sap/import-batch-multi', {
        files: [{ filename: row.filename, content }],
      })
      const updated: UploadResult[] = [...results]
      updated[idx] = res.data.results[0]
      setResults(updated)
      // Totals neu berechnen
      const newTotals = updated.reduce((s, r) => ({
        files: s.files + 1,
        created: s.created + (r.status === 'created' ? 1 : 0),
        duplicates: s.duplicates + (r.status === 'duplicate' ? 1 : 0),
        unknownAccount: s.unknownAccount + (r.status === 'unknown-account' ? 1 : 0),
        errors: s.errors + (r.status === 'error' || r.status === 'no-iban' ? 1 : 0),
      }), { files: 0, created: 0, duplicates: 0, unknownAccount: 0, errors: 0 })
      setTotals(newTotals)
      setSubDialog(null)
      onDone()
    } catch (e: unknown) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      toast({
        title: 'Re-Import fehlgeschlagen',
        description: error?.response?.data?.error || error?.message || 'Unbekannter Fehler',
        tone: 'danger',
      })
    }
  }

  /**
   * CSV ohne IBAN: Hausbankkonten eines Mandanten laden fuer die manuelle Zuordnung.
   */
  async function loadHouseBankAccounts(tenantId: string) {
    if (houseBankAccounts[tenantId]) return
    setLoadingHba(true)
    try {
      const res = await apiClient.get<Array<Record<string, unknown>>>('/buchhaltung/sap/house-bank-accounts', { params: { tenantId } })
      setHouseBankAccounts((prev) => ({ ...prev, [tenantId]: res.data }))
    } catch { /* ignore */ }
    finally { setLoadingHba(false) }
  }

  /**
   * CSV ohne IBAN: Datei mit manuell ausgewaehltem Konto erneut importieren.
   */
  async function retryWithAccount(idx: number, tenantId: string, accountKey: number) {
    if (!results) return
    const row = results[idx]!
    const content = contentCache[row.filename]
    if (!content) {
      toast({
        title: 'Import fehlgeschlagen',
        description: 'Dateiinhalt nicht mehr im Cache – bitte erneut hochladen',
        tone: 'danger',
      })
      return
    }
    try {
      const res = await apiClient.post('/buchhaltung/sap/import-batch-multi', {
        files: [{ filename: row.filename, content }],
        targetTenantId: tenantId,
        targetAccountKey: accountKey,
      })
      const updated: UploadResult[] = [...results]
      updated[idx] = res.data.results[0]
      setResults(updated)
      const newTotals = updated.reduce((s, r) => ({
        files: s.files + 1,
        created: s.created + (r.status === 'created' ? 1 : 0),
        duplicates: s.duplicates + (r.status === 'duplicate' ? 1 : 0),
        unknownAccount: s.unknownAccount + (r.status === 'unknown-account' ? 1 : 0),
        errors: s.errors + (r.status === 'error' || r.status === 'no-iban' ? 1 : 0),
      }), { files: 0, created: 0, duplicates: 0, unknownAccount: 0, errors: 0 })
      setTotals(newTotals)
      setSubDialog(null)
      onDone()
    } catch (e: unknown) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      toast({
        title: 'Import fehlgeschlagen',
        description: error?.response?.data?.error || error?.message || 'Unbekannter Fehler',
        tone: 'danger',
      })
    }
  }

  /**
   * Auto-Post: Alle Zeilen eines importierten Auszugs automatisch gegen
   * offene Rechnungen matchen und buchen.
   */
  async function autoPost(idx: number) {
    if (!results) return
    const row = results[idx]!
    if (!row.tenantId || !row.sapAbsoluteEntry) return
    setAutoPostStatus((prev) => ({ ...prev, [idx]: { loading: true } }))
    try {
      const res = await apiClient.post(`/buchhaltung/sap/statements/${row.tenantId}/${row.sapAbsoluteEntry}/auto-post`)
      setAutoPostStatus((prev) => ({
        ...prev,
        [idx]: {
          result: {
            booked: res.data.booked,
            skipped: res.data.skipped,
            errors: res.data.errors,
            actionable: res.data.actionable,
            matchRate: res.data.matchRate,
            skippedDetails: res.data.details?.skipped ?? [],
          },
        },
      }))
      onDone() // Kontoauszuege neu laden
    } catch (e: unknown) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      setAutoPostStatus((prev) => ({ ...prev, [idx]: {} }))
      toast({
        title: 'Auto-Post fehlgeschlagen',
        description: error?.response?.data?.error || error?.message || 'Unbekannter Fehler',
        tone: 'danger',
      })
    }
  }

  return (
    <>
      <Modal
        open
        onOpenChange={(next) => { if (!next) onClose() }}
        size="xl"
        title="Kontoauszug hochladen"
        description="MT940/CAMT.053-Dateien aus SFirm oder der Bank. Zuordnung zum Konto läuft automatisch per IBAN."
        footer={
          results ? (
            <Button variant="primary" size="sm" onClick={onClose}>Schließen</Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={onClose}>Abbrechen</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={upload}
                disabled={files.length === 0}
                loading={uploading}
                leadingIcon={!uploading ? <Upload className="h-3.5 w-3.5" /> : undefined}
              >
                {uploading ? 'Lädt…' : `${files.length} Datei${files.length === 1 ? '' : 'en'} hochladen`}
              </Button>
            </>
          )
        }
      >
        <div className="space-y-4">
          {!results && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] text-blue-800">
                <strong>Automatische Mandanten-Erkennung:</strong> Die Zuordnung läuft über die IBAN im Statement.
                Wenn ein Konto in keinem Mandanten hinterlegt ist, kannst du es direkt im Ergebnis anlegen.
                <strong> Kreditkarten-CSVs</strong> ohne IBAN können manuell einem Konto zugeordnet werden.
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragging(false)
                  addFiles(e.dataTransfer.files)
                }}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragging ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50'
                }`}
              >
                <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-700 font-medium">Dateien hierhin ziehen oder</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Dateien auswählen
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".sta,.txt,.xml,.mt940,.camt,.cmt,.053,.csv"
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
                <p className="text-xs text-gray-400 mt-2">
                  MT940 (.sta/.txt/.mt940), CAMT.053 (.xml) oder Kreditkarten-CSV (.csv) · max. 50 Dateien
                </p>
              </div>

              {files.length > 0 && (
                <div className="border border-gray-200 rounded">
                  <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-600">
                    {files.length} Datei{files.length === 1 ? '' : 'en'} bereit
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 text-sm">
                        <div className="flex-1 min-w-0 truncate">
                          <span className="text-gray-900">{f.name}</span>
                          <span className="text-xs text-gray-400 ml-2">({(f.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <Button variant="linkDanger" size="xs" onClick={() => removeFile(i)}>
                          Entfernen
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {results && totals && (
            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2 text-center">
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xl font-semibold text-gray-900">{totals.files}</div>
                  <div className="text-[11px] text-gray-500">Dateien</div>
                </div>
                <div className="bg-green-50 rounded p-2">
                  <div className="text-xl font-semibold text-green-700">{totals.created}</div>
                  <div className="text-[11px] text-green-600">Angelegt</div>
                </div>
                <div className="bg-yellow-50 rounded p-2">
                  <div className="text-xl font-semibold text-yellow-700">{totals.duplicates}</div>
                  <div className="text-[11px] text-yellow-600">Vorhanden</div>
                </div>
                <div className="bg-orange-50 rounded p-2">
                  <div className="text-xl font-semibold text-orange-700">{totals.unknownAccount}</div>
                  <div className="text-[11px] text-orange-600">Konto fehlt</div>
                </div>
                <div className="bg-red-50 rounded p-2">
                  <div className="text-xl font-semibold text-red-700">{totals.errors}</div>
                  <div className="text-[11px] text-red-600">Fehler</div>
                </div>
              </div>

              {tenantDiagnostics && tenantDiagnostics.some((d) => d.error !== null) && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px]">
                  <div className="font-semibold text-amber-800 mb-1">
                    SAP-Verbindungsprobleme beim IBAN-Lookup
                  </div>
                  <p className="text-amber-700 mb-2">
                    Für {tenantDiagnostics.filter((d) => d.error !== null).length} Mandant(en) konnten keine HouseBankAccounts geladen werden.
                    Fehlende Konten wurden daher <strong>nicht geprüft</strong> – das kann die Ursache für „Konto fehlt" sein.
                  </p>
                  <div className="space-y-1">
                    {tenantDiagnostics.map((d) => (
                      <div key={d.tenantCode} className="flex items-start gap-2">
                        <span className={`px-1.5 py-0.5 rounded font-medium ${d.error ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {d.tenantCode}
                        </span>
                        {d.error ? (
                          <span className="text-red-700">{d.error}</span>
                        ) : (
                          <span className="text-green-700">{d.loaded} Konto{d.loaded === 1 ? '' : 'en'} geladen</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tenantDiagnostics && !tenantDiagnostics.some((d) => d.error !== null) && totals.unknownAccount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px]">
                  <div className="font-semibold text-amber-800 mb-1">
                    IBAN nicht als HouseBankAccount registriert
                  </div>
                  <p className="text-amber-700 mb-1">
                    SAP-Verbindung zu allen Mandanten war erfolgreich
                    ({tenantDiagnostics.map((d) => `${d.tenantCode}: ${d.loaded}`).join(', ')} Konten).
                    Die fehlende IBAN ist in SAP B1 nicht als HouseBankAccount hinterlegt.
                  </p>
                  <p className="text-amber-700">
                    Bitte in SAP B1 prüfen oder über „Konto anlegen" direkt registrieren.
                  </p>
                </div>
              )}

              <div className="border border-gray-200 rounded overflow-hidden">
                <DataTable<UploadResult>
                  data={results}
                  getRowId={(_r: UploadResult, i: number) => String(i)}
                  enableSorting={false}
                  columns={[
                    {
                      accessorKey: 'filename',
                      header: 'Datei',
                      cell: ({ row }) => (
                        <span className="truncate max-w-[180px] block" title={row.original.filename}>
                          {row.original.filename}
                        </span>
                      ),
                    },
                    {
                      accessorKey: 'detectedIban',
                      header: 'IBAN',
                      cell: ({ row }) => (
                        <span className="text-caption text-gray-500 font-mono">
                          {row.original.detectedIban ? a.iban(row.original.detectedIban) : '–'}
                        </span>
                      ),
                    },
                    {
                      accessorKey: 'tenantCode',
                      header: 'Mandant',
                      cell: ({ row }) => (
                        <span className="text-caption">
                          {row.original.tenantCode
                            ? <span className="px-1.5 py-0.5 bg-gray-100 rounded font-medium">{row.original.tenantCode}</span>
                            : <span className="text-gray-400">–</span>}
                          {row.original.sapStatementNumber && <span className="ml-1 text-gray-500 font-mono">#{row.original.sapStatementNumber}</span>}
                          {!row.original.sapStatementNumber && row.original.sapAbsoluteEntry && <span className="ml-1 text-gray-500 font-mono">#{row.original.sapAbsoluteEntry}</span>}
                        </span>
                      ),
                    },
                    {
                      accessorKey: 'transactions',
                      header: 'Zeilen',
                      cell: ({ row }) => (
                        <span className="block text-right text-gray-700 tabular-nums">{row.original.transactions}</span>
                      ),
                    },
                    {
                      accessorKey: 'status',
                      header: 'Status',
                      cell: ({ row }) => {
                        const r = row.original
                        const i = row.index
                        const variantMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
                          created: 'success',
                          duplicate: 'warning',
                          'no-iban': 'danger',
                          'unknown-account': 'danger',
                          error: 'danger',
                        }
                        const labelMap: Record<string, string> = {
                          created: 'Angelegt',
                          duplicate: 'Bereits vorhanden',
                          'no-iban': 'Keine IBAN',
                          'unknown-account': 'Konto fehlt',
                          error: 'Fehler',
                        }
                        return (
                          <div className="align-top">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant={variantMap[r.status] ?? 'neutral'} size="sm">
                                {labelMap[r.status] ?? r.status}
                              </Badge>
                              {r.format === 'CSV' && (
                                <Badge variant="brand" size="sm">Kreditkarte</Badge>
                              )}
                              {r.status === 'unknown-account' && r.detectedIban && (
                                <Button
                                  variant="link"
                                  size="xs"
                                  onClick={() => setSubDialog({ type: 'create-account', idx: i, iban: r.detectedIban! })}
                                >
                                  Konto anlegen
                                </Button>
                              )}
                              {r.status === 'no-iban' && r.format === 'CSV' && (
                                <Button
                                  variant="link"
                                  size="xs"
                                  onClick={() => {
                                    setSubDialog({ type: 'select-account', idx: i })
                                    if (tenants[0]) loadHouseBankAccounts(tenants[0].id)
                                  }}
                                >
                                  Konto zuordnen
                                </Button>
                              )}
                              {r.status === 'created' && r.tenantId && r.sapAbsoluteEntry && !autoPostStatus[i]?.result && (
                                <Button
                                  variant="link"
                                  size="xs"
                                  onClick={() => autoPost(i)}
                                  disabled={autoPostStatus[i]?.loading}
                                  leadingIcon={autoPostStatus[i]?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                >
                                  {autoPostStatus[i]?.loading ? 'Buche…' : 'Auto-Buchen'}
                                </Button>
                              )}
                            </div>
                            {autoPostStatus[i]?.result && (() => {
                              const r2 = autoPostStatus[i]!.result!
                              const rateStr = r2.matchRate != null ? ` (${r2.matchRate}%)` : ''
                              const total = r2.actionable ?? (r2.booked + r2.skipped)
                              return (
                                <div className="mt-1 text-caption bg-green-50 border border-green-200 rounded p-1.5">
                                  <div>
                                    <CheckCircle2 className="w-3 h-3 inline mr-1 text-green-600" />
                                    <strong>{r2.booked}</strong> von <strong>{total}</strong> Zeilen zugeordnet{rateStr}
                                    {r2.errors > 0 && (
                                      <span className="text-red-600 ml-1">– {r2.errors} Fehler</span>
                                    )}
                                  </div>
                                  {(r2.skippedDetails?.length ?? 0) > 0 && (
                                    <details className="mt-1">
                                      <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">
                                        Details zu übersprungenen Zeilen
                                      </summary>
                                      <ul className="mt-1 space-y-0.5 pl-1">
                                        {r2.skippedDetails!.map((s) => (
                                          <li key={s.lineNum} className="text-gray-600">
                                            <span className="font-medium">Zeile {s.lineNum}:</span> {s.reason}
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  )}
                                </div>
                              )
                            })()}
                            {r.message && r.status !== 'created' && r.status !== 'duplicate' && !autoPostStatus[i]?.result && (
                              <details className="mt-1">
                                <summary className="text-caption text-red-600 cursor-pointer hover:text-red-800">
                                  {r.message.length > 120 ? r.message.slice(0, 120) + '… (klick für Details)' : r.message}
                                </summary>
                                <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-[10px] text-red-900 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">{r.message}</pre>
                              </details>
                            )}
                          </div>
                        )
                      },
                    },
                  ]}
                />
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Sub-Dialog: neues HouseBankAccount in SAP anlegen */}
      {subDialog?.type === 'create-account' && (
        <CreateHouseBankInline
          iban={subDialog.iban}
          tenants={tenants}
          onCreated={() => retryAfterAccountCreation(subDialog.idx)}
          onCancel={() => setSubDialog(null)}
        />
      )}
      {/* Sub-Dialog: CSV ohne IBAN manuell einem Konto zuordnen */}
      {subDialog?.type === 'select-account' && (
        <CsvAccountSelector
          tenants={tenants}
          houseBankAccounts={houseBankAccounts}
          loadingHba={loadingHba}
          onLoadAccounts={loadHouseBankAccounts}
          onSelect={(tenantId, accountKey) => retryWithAccount(subDialog.idx, tenantId, accountKey)}
          onCancel={() => setSubDialog(null)}
        />
      )}
    </>
  )
}

// ─── Kontoauswahl fuer CSV-Dateien ohne IBAN (z.B. Kreditkarten) ─────────────

function CsvAccountSelector({ tenants, houseBankAccounts, loadingHba, onLoadAccounts, onSelect, onCancel }: {
  tenants: Array<{ id: string; code: string; name: string }>
  houseBankAccounts: Record<string, Array<Record<string, unknown>>>
  loadingHba: boolean
  onLoadAccounts: (tenantId: string) => void
  onSelect: (tenantId: string, accountKey: number) => void
  onCancel: () => void
}) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? '')
  const [accountKey, setAccountKey] = useState<number | null>(null)

  useEffect(() => {
    if (tenantId) onLoadAccounts(tenantId)
  }, [tenantId])

  const accounts = houseBankAccounts[tenantId] ?? []

  const tenantOptions = tenants.map((t) => ({ value: t.id, label: `${t.code} · ${t.name}` }))
  const accountOptions = accounts.map((acc) => {
    const key = (acc.AbsEntry ?? acc.AbsoluteEntry ?? acc.InternalKey ?? acc.Key) as number
    const name = String(acc.AccountName ?? acc.Account ?? '')
    const iban = String(acc.IBAN ?? '')
    const gl = String(acc.GLAccount ?? '')
    return {
      value: String(key),
      label: `${name}${gl ? ` (SK ${gl})` : ''}${iban ? ` – ${iban}` : ''}`,
    }
  })

  return (
    <Modal
      open
      onOpenChange={(next) => { if (!next) onCancel() }}
      size="md"
      title="Konto für CSV zuordnen"
      description="CSV ohne IBAN erkannt (z.B. Kreditkartenabrechnung). Das Konto muss vorher in SAP als Hausbankkonto angelegt sein (z.B. Kreditkarte auf Sachkonto 36100)."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onCancel}>Abbrechen</Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!tenantId || accountKey == null}
            onClick={() => accountKey != null && onSelect(tenantId, accountKey)}
            leadingIcon={<Upload className="h-3.5 w-3.5" />}
          >
            Importieren
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Mandant"
          value={tenantId}
          onValueChange={(v) => { setTenantId(v); setAccountKey(null) }}
          options={tenantOptions}
        />
        <Select
          label={<>Konto {loadingHba && <span className="text-gray-400 font-normal">(laden…)</span>}</>}
          value={accountKey != null ? String(accountKey) : undefined}
          onValueChange={(v) => setAccountKey(v ? parseInt(v) : null)}
          placeholder="– Konto wählen –"
          options={accountOptions}
        />
      </div>
    </Modal>
  )
}

// ─── Inline-Formular zum Anlegen eines HouseBankAccounts ───────────────────
// Schlaegt Werte auf Basis der bestehenden Konten des gewaehlten Mandanten vor
// (BLZ aus IBAN, GLAccount-Praefix aus existing Konten, Currency=EUR).

function CreateHouseBankInline({ iban, tenants, onCreated, onCancel }: {
  iban: string
  tenants: Array<{ id: string; code: string; name: string }>
  onCreated: () => void
  onCancel: () => void
}) {
  const { toast } = useToast()
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? '')
  const [existing, setExisting] = useState<Array<Record<string, unknown>>>([])
  const [loadingExisting, setLoadingExisting] = useState(false)
  const [bankCode, setBankCode] = useState('')
  const [account, setAccount] = useState('')
  const [accountName, setAccountName] = useState('')
  const [glAccount, setGlAccount] = useState('')
  const [saving, setSaving] = useState(false)

  // Aus IBAN Bank-BLZ und Kontonummer extrahieren (DE-Format)
  // DE + 2 Pruefziffern + 8 BLZ + 10 Kontonummer
  function parseIban(i: string) {
    const clean = i.replace(/\s+/g, '').toUpperCase()
    if (clean.startsWith('DE') && clean.length === 22) {
      return { blz: clean.slice(4, 12), kto: clean.slice(12).replace(/^0+/, '') }
    }
    return { blz: '', kto: '' }
  }

  useEffect(() => {
    if (!tenantId) return
    setLoadingExisting(true)
    apiClient.get<Array<Record<string, unknown>>>('/buchhaltung/sap/house-bank-accounts', { params: { tenantId } })
      .then((res) => {
        setExisting(res.data)
        // Vorschlaege aus IBAN + bestehenden Konten
        const { blz, kto } = parseIban(iban)
        setBankCode(blz)
        setAccount(kto)
        // GL-Account: nimm den hoechsten numerischen + 1 als Vorschlag (z.B. 18001 → 18002)
        const glNumbers = res.data
          .map((a) => String(a.GLAccount ?? ''))
          .filter((g) => /^\d+$/.test(g))
          .map((g) => parseInt(g, 10))
          .sort((a, b) => b - a)
        if (glNumbers.length > 0) setGlAccount(String(glNumbers[0]! + 1))
        // Namen aus einem existierenden Konto mit gleicher BLZ uebernehmen
        const sameBank = res.data.find((a) => String(a.BankCode ?? '').startsWith(blz))
        if (sameBank) setAccountName(String(sameBank.AccountName ?? ''))
      })
      .catch(() => { /* ignore */ })
      .finally(() => setLoadingExisting(false))
  }, [tenantId, iban])

  async function create() {
    if (!tenantId || !bankCode || !account) {
      toast({
        title: 'Unvollständige Angaben',
        description: 'Mandant, BLZ und Kontonummer sind Pflicht',
        tone: 'warning',
      })
      return
    }
    setSaving(true)
    try {
      await apiClient.post('/buchhaltung/sap/house-bank-accounts', {
        tenantId,
        bankCode,
        account,
        accountName: accountName || undefined,
        iban,
        glAccount: glAccount || undefined,
        currency: 'EUR',
        country: 'DE',
      })
      onCreated()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      toast({
        title: 'Anlage fehlgeschlagen',
        description: err?.response?.data?.error || err?.message || 'Unbekannter Fehler',
        tone: 'danger',
      })
    } finally {
      setSaving(false)
    }
  }

  const tenantOptions = tenants.map((t) => ({ value: t.id, label: `${t.code} · ${t.name}` }))

  return (
    <Modal
      open
      onOpenChange={(next) => { if (!next) onCancel() }}
      size="lg"
      title="Hausbankkonto in SAP anlegen"
      description={
        <>
          IBAN <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">{iban}</code> ist
          in keinem Mandanten hinterlegt. Werte werden anhand bestehender Konten vorgeschlagen.
        </>
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onCancel}>Abbrechen</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={create}
            loading={saving}
            disabled={!tenantId || !bankCode || !account}
            leadingIcon={!saving ? <Save className="h-3.5 w-3.5" /> : undefined}
          >
            In SAP anlegen &amp; Import wiederholen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label="Mandant"
            required
            value={tenantId}
            onValueChange={setTenantId}
            options={tenantOptions}
          />
          <Input
            label={<>Sachkonto {loadingExisting && <span className="text-gray-400 font-normal">(laden…)</span>}</>}
            value={glAccount}
            onChange={(e) => setGlAccount(e.target.value)}
            placeholder="z.B. 18001"
            className="font-mono"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="BLZ / BankCode"
            required
            value={bankCode}
            onChange={(e) => setBankCode(e.target.value)}
            placeholder="z.B. 31060517"
            className="font-mono"
          />
          <Input
            label="Kontonummer"
            required
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            className="font-mono"
          />
        </div>
        <Input
          label="Bank-Bezeichnung"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          placeholder="z.B. Volksbank Mönchengladbach e.G."
        />
        {existing.length > 0 && (
          <div className="text-caption text-gray-500">
            Vorlage aus {existing.length} bestehenden Konten in diesem Mandanten übernommen
            (nächster freier GL-Account).
          </div>
        )}
      </div>
    </Modal>
  )
}
